import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../config/axios';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Save, Plus, Minus, Trash2, Type, CheckSquare, List, MessageSquare, SlidersHorizontal, Upload, Star, ArrowUp, ArrowDown, Table, Palette, Settings, Shuffle, BarChart3, FileSpreadsheet, FileText, X, Copy, Smile } from 'lucide-react';
// Helper global para normalizar emails (bloqueo consistente en todo el archivo)
const normEmail = (e) => String(e || '').trim().toLowerCase();

const estadoColor = estado => {
  if (estado === 'Completado') return { color: '#19B394', fontWeight: 600 };
  if (estado === 'En Curso') return { color: '#007bff', fontWeight: 600 };
  return { color: '#dc3545', fontWeight: 600 };
};

const SurveyBuilder = () => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const navigate = useNavigate();

  const [survey, setSurvey] = useState({
    title: '',
    description: '',
    pages: [] // cada página será { pageTitle, pageDescription, elements: [] }
  });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const titleInputRef = useRef();
  const titleSizerRef = useRef();
  const descRef = useRef();
  const [enableDates, setEnableDates] = useState(false);
  const [enableMaxResponses, setEnableMaxResponses] = useState(false);
  const [enableTimeLimit, setEnableTimeLimit] = useState(false);
  const [enablePerformance, setEnablePerformance] = useState(false);
  const pageTitleRef = useRef();
  const surveyTitleRef = useRef();
  const surveyTitleSizerRef = useRef();
  const questionInputRefs = useRef({});
  const questionSizerRefs = useRef({});
  const MAX_TITLE_WIDTH = 700;
  const MAX_QUESTION_WIDTH = 600;
  const [activeTab, setActiveTab] = useState('diseño');
  const [editingIndex, setEditingIndex] = useState(null);
  const [shared, setShared] = useState([]);
  const [loadingShared, setLoadingShared] = useState(false);
  const [refreshEvaluations, setRefreshEvaluations] = useState(0);


  // Cargar compartidos (bloqueados) al entrar a 'compartir' o 'compartidos'
  useEffect(() => {
    const shouldLoad = (activeTab === 'compartir' || activeTab === 'compartidos') && (editId || survey._id || survey.id);
    if (shouldLoad) {
      setLoadingShared(true);
      const surveyId = editId || survey._id || survey.id;

      if (enablePerformance) {
        // Para evaluaciones de desempeño, forzar refresh del componente
        setRefreshEvaluations(prev => prev + 1);
        setLoadingShared(false);
      } else {
        // Para encuestas normales, cargar desde survey-token
        let url = '/api/survey-token/status';
        if (surveyId) url += `?surveyId=${surveyId}`;
        api.get(url)
          .then(res => setShared(res.data))
          .catch(() => setShared([]))
          .finally(() => setLoadingShared(false));
      }
    } else if (activeTab === 'compartir' || activeTab === 'compartidos') {
      setShared([]); // Limpiar si no hay encuesta
    }
  }, [activeTab, editId, survey._id, survey.id, enablePerformance]);

  // Refrescar automáticamente la tabla de compartidos cada 10 segundos (solo en 'compartidos')
  useEffect(() => {
    let interval;
    if (activeTab === 'compartidos' && (editId || survey._id || survey.id)) {
      interval = setInterval(() => {
        const surveyId = editId || survey._id || survey.id;
        let url = '/api/survey-token/status';
        if (surveyId) url += `?surveyId=${surveyId}`;
        api.get(url)
          .then(res => setShared(res.data))
          .catch(() => { });
      }, 10000);
    }
    return () => clearInterval(interval);
  }, [activeTab, editId, survey._id, survey.id]);

  // Auto-resize para textareas de matriz
  useEffect(() => {
    const handleTextareaResize = (textarea) => {
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(textarea.scrollHeight, 40) + 'px';
    };

    const textareas = document.querySelectorAll('.matrix-col-input-inline, .matrix-row-input-inline');
    textareas.forEach(textarea => {
      textarea.addEventListener('input', () => handleTextareaResize(textarea));
      // Aplicar resize inicial
      handleTextareaResize(textarea);
    });

    return () => {
      textareas.forEach(textarea => {
        textarea.removeEventListener('input', () => handleTextareaResize(textarea));
      });
    };
  }, [survey, currentPage]);

  // Toolbox drag types
  const TOOLBOX_ITEMS = [
    { id: 'toolbox-text', label: 'Abierta', icon: <MessageSquare size={16} />, type: 'text' },
    { id: 'toolbox-multiple_choice', label: 'Opción única', icon: <CheckSquare size={16} />, type: 'multiple_choice' },
    { id: 'toolbox-checkbox', label: 'Múltiples opciones', icon: <List size={16} />, type: 'checkbox' },
    { id: 'toolbox-scale', label: 'Escala', icon: <Star size={16} />, type: 'scale' },
    { id: 'toolbox-slider', label: 'Slider', icon: <SlidersHorizontal size={16} />, type: 'slider' },
    { id: 'toolbox-matrix', label: 'Matriz', icon: <Table size={16} />, type: 'matrix' },
    { id: 'toolbox-file_upload', label: 'Subir archivo', icon: <Upload size={16} />, type: 'file_upload' }
  ];

  useEffect(() => {
    if (editId) {
      fetchSurvey(editId);
    }
  }, [editId]);

  useEffect(() => {
    // Carga inicial de compartidos al montar o cambiar encuesta
    const fetchShared = async () => {
      setLoadingShared(true);
      let url = '/api/survey-token/status';
      const surveyId = editId || survey._id || survey.id;
      if (surveyId) url += `?surveyId=${surveyId}`;
      try {
        const res = await api.get(url);
        setShared(Array.isArray(res.data) ? res.data : []);
      } catch {
        setShared([]);
      } finally {
        setLoadingShared(false);
      }
    };
    fetchShared();
  }, [editId, survey._id, survey.id]);


  // Ajustar ancho del input de título
  useEffect(() => {
    if (titleInputRef.current && titleSizerRef.current) {
      const width = titleSizerRef.current.offsetWidth + 24; // padding
      titleInputRef.current.style.width = Math.min(width, titleInputRef.current.parentNode.offsetWidth) + 'px';
    }
  }, [survey.title, activeTab]);

  // Ajustar altura del textarea de descripción
  useEffect(() => {
    if (descRef.current) {
      descRef.current.style.height = 'auto';
      descRef.current.style.height = descRef.current.scrollHeight + 'px';
    }
  }, [survey.description, activeTab]);

  // Ajustar altura del textarea de descripción de la página
  useEffect(() => {
    const pageDesc = document.querySelector('.page-description-input');
    if (pageDesc) {
      pageDesc.style.height = 'auto';
      pageDesc.style.height = pageDesc.scrollHeight + 'px';
    }
  }, [survey.pages, currentPage, survey.pages[currentPage]?.pageDescription, activeTab]);

  useEffect(() => {
    if (pageTitleRef.current) {
      pageTitleRef.current.style.height = 'auto';
      pageTitleRef.current.style.height = pageTitleRef.current.scrollHeight + 'px';
    }
  }, [survey.pages[currentPage]?.pageTitle, currentPage, activeTab]);

  useEffect(() => {
    if (surveyTitleRef.current && surveyTitleSizerRef.current) {
      // Ajustar ancho dinámico
      const width = surveyTitleSizerRef.current.offsetWidth + 24; // padding
      const maxWidth = Math.min(surveyTitleRef.current.parentNode.offsetWidth, MAX_TITLE_WIDTH);
      const finalWidth = Math.min(width, maxWidth);
      surveyTitleRef.current.style.width = finalWidth + 'px';
      // Ajustar altura si llegó al máximo de ancho
      if (width >= maxWidth) {
        surveyTitleRef.current.style.height = 'auto';
        surveyTitleRef.current.style.height = surveyTitleRef.current.scrollHeight + 'px';
      } else {
        surveyTitleRef.current.style.height = '60px';
      }
    }
  }, [survey.title, activeTab]);

  // Auto-resize para inputs de pregunta
  useEffect(() => {
    if (survey.pages && survey.pages[currentPage] && survey.pages[currentPage].elements) {
      survey.pages[currentPage].elements.forEach((element) => {
        const inputRef = questionInputRefs.current[element.id];
        const sizerRef = questionSizerRefs.current[element.id];

        if (inputRef && sizerRef) {
          const width = sizerRef.offsetWidth + 3; // padding
          const maxWidth = Math.min(inputRef.parentNode.offsetWidth - 30, MAX_QUESTION_WIDTH);
          const finalWidth = Math.min(width, maxWidth);
          inputRef.style.width = finalWidth + 'px';
        }
      });
    }
  }, [survey.pages, currentPage, activeTab]);

  // Al cargar una encuesta existente, asegura que todas las preguntas tengan id único
  const ensureQuestionIds = (surveyData) => {
    const pages = (surveyData.pages || []).map(page => ({
      ...page,
      elements: (page.elements || []).map(q => ({
        ...q,
        id: q.id || Date.now().toString() + Math.random().toString(36).slice(2)
      }))
    }));
    const questions = (surveyData.questions || []).map(q => ({
      ...q,
      id: q.id || Date.now().toString() + Math.random().toString(36).slice(2)
    }));
    return { ...surveyData, pages, questions };
  };

  const fetchSurvey = async (id) => {
    try {
      const response = await api.get(`/api/forms/${id}`);
      let data = response.data;

      // --- TRANSFORMACIÓN ÚNICA Y FINAL ---
      if (Array.isArray(data.pages) && data.pages.length && data.pages[0].questions) {
        data.pages = data.pages.map(pageObj => ({
          pageTitle: pageObj.pageTitle || '',
          pageDescription: pageObj.pageDescription || '',
          elements: pageObj.questions || []
        }));
      }
      // Asegura que todas las preguntas tengan id único
      data = ensureQuestionIds(data);
      setSurvey(prev => ({
        ...data,
        startDateOnly: data.startDate ? data.startDate.split('T')[0] : '',
        startTimeOnly: data.startDate ? (data.startDate.split('T')[1] || '').slice(0, 5) : '',
        endDateOnly: data.endDate ? data.endDate.split('T')[0] : '',
        endTimeOnly: data.endDate ? (data.endDate.split('T')[1] || '').slice(0, 5) : '',
        maxResponses: data.maxResponses || '',
        timeLimit: data.timeLimit || '',
        performance: data.performance || false,
      }));
      setEnableDates(!!(data.startDate || data.endDate));
      setEnableMaxResponses(!!data.maxResponses);
      setEnableTimeLimit(!!data.timeLimit);
      setEnablePerformance(!!data.performance);
    } catch (error) {
      console.error('Error al obtener encuesta:', error);
      toast.error('No se pudo obtener la encuesta');
      navigate('/dashboard');
    }
  };

  const addQuestion = (type) => {
    const newQuestion = {
      id: Date.now().toString() + Math.random().toString(36).slice(2), // id único
      type,
      title: '',
      required: false,
      options: (type === 'multiple_choice' || type === 'checkbox' || type === 'ranking') ? ['', ''] : [],
      min: type === 'scale' ? 1 : undefined,
      max: type === 'scale' ? 5 : undefined,
      leftLabel: type === 'scale' ? 'Nada probable' : undefined,
      rightLabel: type === 'scale' ? 'Muy probable' : undefined,
      scaleType: type === 'scale' ? 'numbers' : undefined
    };
    if (type === 'matrix') {
      newQuestion.rows = ['', ''];
      newQuestion.columns = ['', ''];
    }
    if (type === 'slider') {
      newQuestion.sliders = [
        { label: '', value: 0, min: 0, max: 100 }
      ];
    }
    setSurvey(prev => {
      const newPages = [...prev.pages];
      if (!newPages[currentPage]) newPages[currentPage] = { pageTitle: '', pageDescription: '', elements: [] };
      const elements = Array.isArray(newPages[currentPage].elements) ? newPages[currentPage].elements : [];
      newPages[currentPage] = {
        ...newPages[currentPage],
        elements: [...elements, newQuestion]
      };
      return { ...prev, pages: newPages };
    });
  };

  const updateQuestion = (pageIndex, questionIndex, field, value) => {
    setSurvey(prev => ({
      ...prev,
      pages: prev.pages.map((page, i) =>
        i === pageIndex ? {
          ...page,
          elements: page.elements.map((q, j) =>
            j === questionIndex ? { ...q, [field]: value } : q
          )
        } : page
      )
    }));
  };

  const removeQuestion = (pageIndex, questionIndex) => {
    setSurvey(prev => ({
      ...prev,
      pages: prev.pages.map((page, i) =>
        i === pageIndex ? {
          ...page,
          elements: page.elements.filter((_, j) => j !== questionIndex)
        } : page
      )
    }));
  };

  const duplicateQuestion = (pageIndex, questionIndex) => {
    setSurvey(prev => {
      const originalQuestion = prev.pages[pageIndex].elements[questionIndex];
      const duplicatedQuestion = {
        ...originalQuestion,
        id: Date.now().toString() + Math.random().toString(36).slice(2), // nuevo id único
        title: originalQuestion.title // mantener el mismo título
      };

      return {
        ...prev,
        pages: prev.pages.map((page, i) =>
          i === pageIndex ? {
            ...page,
            elements: [
              ...page.elements.slice(0, questionIndex + 1),
              duplicatedQuestion,
              ...page.elements.slice(questionIndex + 1)
            ]
          } : page
        )
      };
    });
  };

  const addOption = (pageIndex, questionIndex) => {
    setSurvey(prev => ({
      ...prev,
      pages: prev.pages.map((page, i) =>
        i === pageIndex ? {
          ...page,
          elements: page.elements.map((q, j) =>
            j === questionIndex ? { ...q, options: [...q.options, ''] } : q
          )
        } : page
      )
    }));
  };

  const updateOption = (pageIndex, questionIndex, optionIndex, value) => {
    setSurvey(prev => ({
      ...prev,
      pages: prev.pages.map((page, i) =>
        i === pageIndex ? {
          ...page,
          elements: page.elements.map((q, j) =>
            j === questionIndex ? {
              ...q,
              options: q.options.map((opt, k) =>
                k === optionIndex ? value : opt
              )
            } : q
          )
        } : page
      )
    }));
  };

  const removeOption = (pageIndex, questionIndex, optionIndex) => {
    setSurvey(prev => ({
      ...prev,
      pages: prev.pages.map((page, i) =>
        i === pageIndex ? {
          ...page,
          elements: page.elements.map((q, j) =>
            j === questionIndex ? {
              ...q,
              options: q.options.filter((_, k) => k !== optionIndex)
            } : q
          )
        } : page
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!survey.title.trim()) {
      toast.error('El título de la encuesta es obligatorio');
      return;
    }
    if ((survey.pages?.length || 0) === 0 || survey.pages.some(page => (page.elements?.length || 0) === 0)) {
      toast.error('Se requiere al menos una pregunta');
      return;
    }
    setLoading(true);
    // Genero questions plano para compatibilidad
    const flatQuestions = survey.pages.flatMap(page => page.elements);
    // --- GUARDAR CAMPOS OPCIONALES SOLO SI ESTÁN HABILITADOS ---
    const surveyToSave = {
      ...survey,
      questions: flatQuestions,
      pages: survey.pages.map(page => ({
        pageTitle: page.pageTitle || '',
        pageDescription: page.pageDescription || '',
        questions: page.elements || []
      })),
      startDate: enableDates ? `${survey.startDateOnly || ''}T${survey.startTimeOnly || ''}` : '',
      endDate: enableDates ? `${survey.endDateOnly || ''}T${survey.endTimeOnly || ''}` : '',
      maxResponses: enableMaxResponses ? survey.maxResponses || '' : '',
      timeLimit: enableTimeLimit ? survey.timeLimit || '' : '',
      performance: enablePerformance,
    };

    try {
      if (editId) {
        const response = await api.put(`/api/forms/${editId}`, surveyToSave);
        toast.success('Encuesta actualizada exitosamente');
      } else {
        const response = await api.post('/api/forms', surveyToSave);
        toast.success('Encuesta creada exitosamente');
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('No se pudo guardar la encuesta');
    } finally {
      setLoading(false);
    }
  };

  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case 'text': return <MessageSquare size={16} />;
      case 'multiple_choice': return <CheckSquare size={16} />;
      case 'checkbox': return <List size={16} />;
      case 'scale': return <SlidersHorizontal size={16} />;
      default: return <Type size={16} />;
    }
  };

  const getQuestionTypeName = (type) => {
    switch (type) {
      case 'text': return 'Respuesta abierta';
      case 'multiple_choice': return 'Opción única';
      case 'checkbox': return 'Múltiples opciones';
      case 'scale': return 'Escala';
      default: return 'Respuesta abierta';
    }
  };

  const addPage = () => {
    setSurvey(prev => ({
      ...prev,
      pages: [...prev.pages, { pageTitle: '', pageDescription: '', elements: [] }]
    }));
    setCurrentPage((survey.pages?.length || 0));
  };

  const deletePage = () => {
    setSurvey(prev => ({
      ...prev,
      pages: prev.pages.filter((_, idx) => idx !== currentPage)
    }));
    setCurrentPage(0);
  };

  // Estado para emails seleccionados y loading
  const [selectedRows, setSelectedRows] = useState([]); // [{ name, email, dni }]
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // normEmail ahora está definido a nivel de módulo

  // Calcular máximo permitido
  const maxRespuestas = enableMaxResponses && Number(survey.maxResponses) > 0 ? Number(survey.maxResponses) : null;
  const enviadosCount = shared.length;

  // Handler para checkboxes en la tabla
  const handleCheckboxChange = (row, checked) => {
    console.log('ROW SELECCIONADO:', row); // DEPURACIÓN
    // Normalizar la estructura del row para compatibilidad y orden igual que desempeño
    const normalizedRow = {
      company: row.company?.name || row.company || '',
      sede: row.sede?.name || row.sede || '',
      department: row.department?.name || row.department || '',
      area: row.area?.name || row.area || '',
      section: row.section?.name || row.section || '',
      occupation: row.occupationGroup?.name || row.occupation || row['Ocupacion'] || '',
      name: row.person?.name || row.name,
      dni: row.person?.id || row.dni,
      email: row.person?.email || row.email,
      companyEntryDate: row.companyEntryDate || row.fechaIngreso || ''
    };

    if (checked) {
      if (maxRespuestas && enviadosCount + selectedRows.length + 1 > maxRespuestas) {
        toast.error('Se ha alcanzado el máximo de destinatarios permitidos para esta encuesta.');
        return;
      }
      setSelectedRows(prev => {
        if (prev.some(r => String(r.dni) === String(normalizedRow.dni))) return prev;
        return [...prev, normalizedRow];
      });
    } else {
      setSelectedRows(prev => prev.filter(r => String(r.dni) !== String(normalizedRow.dni)));
    }
  };

  // Handler para enviar encuesta
  const handleSendSurvey = async () => {
    // Validar existencia de ID de encuesta
    if (!(survey._id || survey.id || editId)) {
      setSendResult({ success: false, message: 'Aún no ha creado la encuesta' });
      return;
    }
    // Solo enviar a los seleccionados cuyo DNI no haya sido enviado antes
    const enviadosDniSet = new Set(shared.map(row => String(row.dni)));
    const recipientsToSend = selectedRows.filter(r => !enviadosDniSet.has(String(r.dni))).map(r => ({
      name: r.name,
      dni: r.dni,
      email: r.email,
      company: typeof r.company === 'object' && r.company !== null ? r.company.name || '' : (r.company || ''),
      sede: typeof r.sede === 'object' && r.sede !== null ? r.sede.name || '' : (r.sede || ''),
      department: typeof r.department === 'object' && r.department !== null ? r.department.name || '' : (r.department || ''),
      area: typeof r.area === 'object' && r.area !== null ? r.area.name || '' : (r.area || ''),
      section: typeof r.section === 'object' && r.section !== null ? r.section.name || '' : (r.section || ''),
      occupation: r.occupation || ''
    }));
    if ((recipientsToSend?.length || 0) === 0) {
      setSendResult({ success: false, message: 'No ha seleccionado a ningún destinatario' });
      return;
    }
    if (maxRespuestas && enviadosCount + recipientsToSend.length > maxRespuestas) {
      setSendResult({ success: false, message: 'Se ha alcanzado el máximo de destinatarios permitidos para esta encuesta.' });
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const BATCH_SIZE = 100;
      let sent = 0;
      let failed = 0;
      const allResults = [];
      for (let i = 0; i < recipientsToSend.length; i += BATCH_SIZE) {
        const chunk = recipientsToSend.slice(i, i + BATCH_SIZE);
        try {
          const res = await api.post('/api/send-survey-emails', {
            recipients: chunk,
            surveyId: survey._id || survey.id
          });
          const data = res.data || {};
          const results = Array.isArray(data.results) ? data.results : [];
          allResults.push(...results);
          sent += results.filter(r => r.status === 'sent').length;
          failed += results.filter(r => r.status === 'error').length;
        } catch (e) {
          failed += chunk.length;
          allResults.push(...chunk.map(r => ({ email: r.email, status: 'error', error: 'network' })));
        }
      }

      const selected = recipientsToSend.length;
      const processed = allResults.length; // después de validación/deduplicación en backend
      const skipped = Math.max(0, selected - processed); // inválidos/duplicados

      if (sent > 0) {
        setSendResult({ success: true, message: `Enviados ${sent}. Fallidos ${failed}. Seleccionados ${selected}. Omitidos ${skipped}.` });
        // Refrescar 'shared' desde el servidor para reflejar sólo los realmente enviados
        try {
          const surveyId = editId || survey._id || survey.id;
          let url = '/api/survey-token/status';
          if (surveyId) url += `?surveyId=${surveyId}`;
          const fresh = await api.get(url);
          setShared(Array.isArray(fresh.data) ? fresh.data : []);
        } catch (_) { /* ignore */ }
        // Quitar de seleccionados los que sí fueron enviados
        const sentEmailsSet = new Set(
          allResults
            .filter(x => x.status === 'sent')
            .map(x => String(x.email || '').trim().toLowerCase())
        );
        setSelectedRows(prev => prev.filter(r => !sentEmailsSet.has(String(r.email || '').trim().toLowerCase())));
      } else {
        setSendResult({ success: false, message: `No se pudo enviar ningún correo. Seleccionados ${selected}. Omitidos ${skipped}.` });
      }
    } catch (err) {
      setSendResult({ success: false, message: 'Error de red o del servidor.' });
    } finally {
      setSending(false);
    }
    console.log('selectedRows:', selectedRows); // <-- DEPURACIÓN
  };

  // Después de setSendResult(...), si el mensaje es de éxito, ocultar tras 2 segundos
  useEffect(() => {
    if (sendResult) {
      const timer = setTimeout(() => setSendResult(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [sendResult]);

  // Agregar funciones para mover preguntas
  const moveQuestionUp = (pageIndex, questionIndex) => {
    if (questionIndex === 0) return;
    setSurvey(prev => {
      const newPages = prev.pages.map((page, idx) => {
        if (idx !== pageIndex) return page;
        const elements = [...page.elements];
        [elements[questionIndex - 1], elements[questionIndex]] = [elements[questionIndex], elements[questionIndex - 1]];
        return { ...page, elements };
      });
      return { ...prev, pages: newPages };
    });
  };

  const moveQuestionDown = (pageIndex, questionIndex) => {
    setSurvey(prev => {
      const newPages = prev.pages.map((page, idx) => {
        if (idx !== pageIndex) return page;
        const elements = [...page.elements];
        if (questionIndex === elements.length - 1) return page;
        [elements[questionIndex + 1], elements[questionIndex]] = [elements[questionIndex], elements[questionIndex + 1]];
        return { ...page, elements };
      });
      return { ...prev, pages: newPages };
    });
  };

  // IDs de usuarios ya enviados (compartidos)
  const enviadosEmail = new Set(shared.map(row => normEmail(row.email)));
  const enviadosDni = new Set(shared.map(row => String(row.dni)));
  const enviadosMap = new Map(shared.map(row => [row.dni, row._id]));

  // Mueve aquí la función para eliminar de compartidos
  const handleDeleteCompartido = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este registro de compartidos?')) return;
    const deleted = shared.find(row => row._id === id);
    await api.delete(`/api/survey-token/${id}`);
    setShared(prev => prev.filter(row => row._id !== id));
    // Elimina también de selectedRows si estaba seleccionado
    if (deleted) {
      setSelectedRows(prev => prev.filter(r => String(r.dni) !== String(deleted.dni)));
    }
  };

  // Refrescar compartidos al recibir evento global
  useEffect(() => {
    const handleRefreshCompartidos = () => {
      const surveyId = editId || survey._id || survey.id;
      let url = '/api/survey-token/status';
      if (surveyId) url += `?surveyId=${surveyId}`;
      setLoadingShared(true);
      api.get(url)
        .then(res => setShared(res.data))
        .catch(() => setShared([]))
        .finally(() => setLoadingShared(false));
    };
    window.addEventListener('refreshCompartidos', handleRefreshCompartidos);
    return () => window.removeEventListener('refreshCompartidos', handleRefreshCompartidos);
  }, [editId, survey._id, survey.id]);

  // Función para exportar compartidos a Excel usando el backend
  const exportCompartidosToExcel = useCallback(async () => {
    try {
      const surveyId = editId || survey._id || survey.id;
      if (!surveyId) {
        toast.error('No se pudo obtener el ID de la encuesta');
        return;
      }

      const response = await api.get(`/api/survey-token/export-compartidos/${surveyId}`, { 
        responseType: 'blob' 
      });
      
      const contentType = response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Obtener el nombre del archivo desde el header Content-Disposition
      const contentDisposition = response.headers?.['content-disposition'];
      let filename = `${survey.title || 'Compartidos'}_Compartidos.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado correctamente');
    } catch (err) {
      console.error('Error exportando Excel:', err);
      toast.error('No se pudo exportar a Excel');
    }
  }, [editId, survey._id, survey.id, survey.title]);

  // Event listener para exportar compartidos
  useEffect(() => {
    const handler = () => exportCompartidosToExcel();
    window.addEventListener('compartidos-export-excel', handler);
    return () => window.removeEventListener('compartidos-export-excel', handler);
  }, [exportCompartidosToExcel]);

  return (

    <div className="survey-grid-container">
      <div className="survey-menu">
        <div className="tab-menu">
          <button type="button" className="tab-menu-opcion" onClick={() => setActiveTab('diseño')}>
            <Palette size={30} style={{ marginRight: '5px', color: '#19B394' }} />
            Diseño
          </button>
          {/* Eliminar el botón de Configuración */}
          <button type="button" className="tab-menu-opcion" onClick={() => setActiveTab('compartir')}>
            <Shuffle size={30} style={{ marginRight: '5px', color: '#19B394' }} />
            Compartir
          </button>
          <button type="button" className="tab-menu-opcion" onClick={() => setActiveTab('compartidos')}>
            <BarChart3 size={30} style={{ marginRight: '5px', color: '#19B394' }} />
            Compartidos
          </button>
        </div>
      </div>
      <div className="survey-principal">
        {activeTab === 'diseño' && (
          <div className="survey-diseño" >
            <div className="survey-sidebar">
              {/* Herramientas */}
              <div className="sidebar-question-types">
                <h3 className="sidebar-title">Herramientas</h3>
                <div className='question-type-selector' style={{ minHeight: 40 }}>
                  {TOOLBOX_ITEMS.map((item, idx) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => addQuestion(item.type)}
                      className="question-type-btn left-align"
                      style={{ marginBottom: 4, display: 'flex', alignItems: 'center', width: '100%' }}
                    >
                      {item.icon} {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="survey-main" >
              <div className="builder-container">
                <form onSubmit={handleSubmit}>
                  {/* Encabezado editable: los inputs son el título y la descripción */}
                  <div className="survey-header-block">
                    <div className="survey-title-center">
                      <input
                        ref={surveyTitleRef}
                        type="text"
                        value={survey.title}
                        onChange={e => setSurvey(prev => ({ ...prev, title: e.target.value }))}
                        className="survey-title-input"
                        placeholder="Título de la encuesta"
                        required
                        autoFocus
                      />
                      {/* Span invisible para medir el ancho del texto */}
                      <span ref={surveyTitleSizerRef} style={{
                        position: 'absolute',
                        visibility: 'hidden',
                        height: 0,
                        overflow: 'hidden',
                        whiteSpace: 'pre',
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        fontFamily: 'Segoe UI, Arial, sans-serif',
                        padding: '18px 10px',
                        maxWidth: MAX_TITLE_WIDTH + 'px',
                        boxSizing: 'border-box',
                        wordBreak: 'break-all',
                      }}>{survey.title || 'Título de la encuesta'}</span>
                    </div>
                    <textarea
                      value={survey.description}
                      onChange={(e) => setSurvey(prev => ({ ...prev, description: e.target.value }))}
                      className="survey-description-input"
                      placeholder="Descripción"
                      rows="1"
                      ref={descRef}
                      style={{}}
                    />
                  </div>

                  <div className="page-controls" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: 12,
                    marginBottom: 24,
                    justifyItems: 'center'
                  }}>
                    {survey.pages.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setCurrentPage(idx)}
                        className={`page-btn${currentPage === idx ? ' active' : ''}`}
                        style={{ minWidth: 130, maxWidth: 130 }}
                      >
                        Página {idx + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={addPage}
                      className="add-page-btn"
                      style={{ minWidth: 130, maxWidth: 130 }}
                    >
                      + Página
                    </button>
                    <button
                      type="button"
                      onClick={deletePage}
                      className="delete-page-btn"
                      style={{ display: (survey.pages?.length || 0) > 1 ? 'inline-block' : 'none', minWidth: 130, maxWidth: 130 }}
                    >
                      Eliminar
                    </button>
                  </div>

                  {survey.pages[currentPage] && (
                    <div className="page-header-block">
                      <textarea
                        ref={pageTitleRef}
                        className="page-title-input"
                        value={survey.pages[currentPage]?.pageTitle || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setSurvey(prev => ({
                            ...prev,
                            pages: prev.pages.map((page, idx) => idx === currentPage ? { ...page, pageTitle: value } : page)
                          }));
                        }}
                        placeholder="Nombre de la página"
                        rows={1}
                      />
                      <textarea
                        className="page-description-input"
                        value={survey.pages[currentPage]?.pageDescription || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setSurvey(prev => ({
                            ...prev,
                            pages: prev.pages.map((page, idx) => idx === currentPage ? { ...page, pageDescription: value } : page)
                          }));
                        }}
                        placeholder="Descripción de la página"
                        rows={1}
                        style={{}}
                      />
                    </div>
                  )}

                  <div className="card" style={{ display: 'flex', flexDirection: 'row', gap: '32px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {survey.pages.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          <p>No hay preguntas aún. Agrega tu primera pregunta usando la barra lateral.</p>
                        </div>
                      ) : (
                        <div key={currentPage} className="page-builder">
                          {(survey.pages[currentPage]?.elements?.length || 0) === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                              <p>No hay preguntas aún. Agrega tu primera pregunta usando la barra lateral.</p>
                            </div>
                          ) : (
                            <div className="question-list">
                              {survey.pages[currentPage]?.elements?.map((element, elementIndex) => (
                                <div key={element.id} className="question-item">
                                  <div className="question-builder">

                                    <div className="form-group">
                                      <div className="question-title-row" style={{ width: '100%', maxWidth: 618, position: 'relative' }}>
                                        <input
                                          ref={(el) => {
                                            if (el) questionInputRefs.current[element.id] = el;
                                          }}
                                          type="text"
                                          value={element.title}
                                          onChange={(e) => updateQuestion(currentPage, elementIndex, 'title', e.target.value)}
                                          className="form-label"
                                          placeholder='Pregunta'
                                          style={{
                                            display: 'inline-block',
                                            width: 'auto',
                                            maxWidth: 'calc(100% - 30px)',
                                            whiteSpace: 'nowrap',
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            textOverflow: 'clip',
                                            boxSizing: 'border-box'
                                          }}
                                        />
                                        {/* Span invisible para medir el ancho del texto */}
                                        <span
                                          ref={(el) => {
                                            if (el) questionSizerRefs.current[element.id] = el;
                                          }}
                                          style={{
                                            position: 'absolute',
                                            visibility: 'hidden',
                                            height: 0,
                                            overflow: 'hidden',
                                            whiteSpace: 'pre',
                                            fontSize: '16px',
                                            fontFamily: 'Segoe UI, Arial, sans-serif',
                                            padding: '8px 12px',
                                            maxWidth: MAX_QUESTION_WIDTH + 'px',
                                            boxSizing: 'border-box',
                                            wordBreak: 'break-all',
                                          }}
                                        >
                                          {element.title || 'Pregunta'}
                                        </span>
                                        {element.required && (
                                          <span className="required-asterisk" title="Requerido">*</span>
                                        )}
                                      </div>
                                      {element.type === 'text' && (
                                        <div className="text-input">
                                          <input
                                            type="text"
                                            disabled
                                            className="form-input"
                                          />
                                        </div>
                                      )}
                                    </div>

                                    {element.type === 'multiple_choice' && (
                                      <div className="choice-builder-container">
                                        {element.options.map((option, optionIndex) => (
                                          <div key={optionIndex} className="option-builder">
                                            <input
                                              className="option-radio"
                                              type="radio"
                                              disabled
                                            />
                                            <input
                                              type="text"
                                              value={option}
                                              onChange={(e) => updateOption(currentPage, elementIndex, optionIndex, e.target.value)}
                                              className="option-edit-input"
                                              placeholder={`Opción ${optionIndex + 1}`}
                                              required
                                            />
                                            <button
                                              type="button"
                                              onClick={() => removeOption(currentPage, elementIndex, optionIndex)}
                                              className="remove-btn"
                                              disabled={(element.options?.length || 0) <= 2}
                                              style={{ marginLeft: 8 }}
                                            >
                                              ×
                                            </button>
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() => addOption(currentPage, elementIndex)}
                                          className="add-option-btn"
                                        >
                                          <Plus size={14} style={{ marginRight: '5px' }} />
                                          Agregar opción
                                        </button>
                                      </div>
                                    )}
                                    {element.type === 'checkbox' && (
                                      <div className="checkbox-builder-container">
                                        {element.options.map((option, optionIndex) => (
                                          <div key={optionIndex} className="option-builder">
                                            <input
                                              className="option-checkbox"
                                              type="checkbox"
                                              disabled
                                            />
                                            <input
                                              type="text"
                                              value={option}
                                              onChange={(e) => updateOption(currentPage, elementIndex, optionIndex, e.target.value)}
                                              className="option-edit-input"
                                              placeholder={`Opción ${optionIndex + 1}`}
                                              required
                                            />
                                            <button
                                              type="button"
                                              onClick={() => removeOption(currentPage, elementIndex, optionIndex)}
                                              className="remove-btn"
                                              disabled={(element.options?.length || 0) <= 2}
                                              style={{ marginLeft: 8 }}
                                            >
                                              ×
                                            </button>
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() => addOption(currentPage, elementIndex)}
                                          className="add-option-btn"
                                        >
                                          <Plus size={14} style={{ marginRight: '5px' }} />
                                          Agregar opción
                                        </button>
                                      </div>
                                    )}
                                    {element.type === 'scale' && (
                                      <div className="scale-builder-container">
                                        <div className="scale-range-row" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                          <button
                                            type="button"
                                            title="Disminuir"
                                            onClick={() => {
                                              const next = Math.max(2, (element.max ?? 5) - 1);
                                              updateQuestion(currentPage, elementIndex, 'min', 1);
                                              updateQuestion(currentPage, elementIndex, 'max', next);
                                            }}
                                            disabled={(element.max ?? 5) <= 2}
                                            className='scale-btn scale-btn--minus'
                                          >
                                            <Minus size={14} />
                                          </button>
                                          <button
                                            type="button"
                                            title="Aumentar"
                                            onClick={() => {
                                              const next = Math.min(10, (element.max ?? 5) + 1);
                                              updateQuestion(currentPage, elementIndex, 'min', 1);
                                              updateQuestion(currentPage, elementIndex, 'max', next);
                                            }}
                                            disabled={(element.max ?? 5) >= 10}
                                            className='scale-btn scale-btn--plus'
                                          >
                                            <Plus size={14} />
                                          </button>
                                        </div>
                                        <div className="scale-preview-row" style={{ marginTop: 10 }}>
                                          <div className="scale-preview-options">
                                            {(() => {
                                              const total = Math.max(2, Math.min(10, (element.max ?? 5)));
                                              const scaleType = element.scaleType || 'numbers';

                                              return Array.from({ length: total }).map((_, idx) => {
                                                const value = 1 + idx;
                                                return (
                                                  <div key={value} className="scale-preview-option">
                                                    {scaleType === 'numbers' && (
                                                      <div className="scale-number-circle">{value}</div>
                                                    )}
                                                    {scaleType === 'stars' && (
                                                      <div className="scale-star-container">
                                                        <Star
                                                          size={48}
                                                          fill={idx < 1 ? '#FFD700' : 'none'}
                                                          color="#FFD700"
                                                        />
                                                      </div>
                                                    )}
                                                    {scaleType === 'faces' && (
                                                      <div className="scale-face-container">
                                                        {(() => {
                                                          const getFaceIcon = (index, total) => {
                                                            const progress = index / (total - 1);

                                                            // Usar iconos de Google Material Symbols
                                                            const getMaterialIcon = (iconName) => (
                                                              <span className="material-symbols-outlined" style={{ fontSize: '48px', color: '#9ca3af' }}>
                                                                {iconName}
                                                              </span>
                                                            );

                                                            if (total === 10) {
                                                              // Para 10 caritas usando Material Symbols
                                                              const faceIcons = [
                                                                'sentiment_very_dissatisfied',      // 1
                                                                'sentiment_extremely_dissatisfied', // 2
                                                                'sentiment_stressed',               // 3
                                                                'sentiment_sad',                    // 4
                                                                'sentiment_dissatisfied',           // 5
                                                                'sentiment_neutral',                // 6
                                                                'sentiment_satisfied',              // 7
                                                                'mood',                             // 8
                                                                'sentiment_very_satisfied',         // 9
                                                                'sentiment_excited'                 // 10
                                                              ];
                                                              return getMaterialIcon(faceIcons[index] || faceIcons[0]);
                                                            }

                                                            if (total === 9) {
                                                              // Omitir 'sentiment_extremely_dissatisfied' (posición 2 de 10)
                                                              const faceIcons = [
                                                                'sentiment_very_dissatisfied',      // 1
                                                                'sentiment_stressed',               // 3
                                                                'sentiment_sad',                    // 4
                                                                'sentiment_dissatisfied',           // 5
                                                                'sentiment_neutral',                // 6
                                                                'sentiment_satisfied',              // 7
                                                                'mood',                             // 8
                                                                'sentiment_very_satisfied',         // 9
                                                                'sentiment_excited'                 // 10
                                                              ];
                                                              return getMaterialIcon(faceIcons[index] || faceIcons[0]);
                                                            }

                                                            if (total === 8) {
                                                              // Omitir posiciones 2 y 3 de 10
                                                              const faceIcons = [
                                                                'sentiment_very_dissatisfied',      // 1
                                                                'sentiment_sad',                    // 4
                                                                'sentiment_dissatisfied',           // 5
                                                                'sentiment_neutral',                // 6
                                                                'sentiment_satisfied',              // 7
                                                                'mood',                             // 8
                                                                'sentiment_very_satisfied',         // 9
                                                                'sentiment_excited'                 // 10
                                                              ];
                                                              return getMaterialIcon(faceIcons[index] || faceIcons[0]);
                                                            }

                                                            if (total === 7) {
                                                              // Omitir posiciones 2, 3 y 4 de 10
                                                              const faceIcons = [
                                                                'sentiment_very_dissatisfied',      // 1
                                                                'sentiment_sad',                    // 4
                                                                'sentiment_dissatisfied',           // 5
                                                                'sentiment_neutral',                // 6
                                                                'sentiment_satisfied',              // 7
                                                                'sentiment_very_satisfied',         // 9
                                                                'sentiment_excited'                 // 10
                                                              ];
                                                              return getMaterialIcon(faceIcons[index] || faceIcons[0]);
                                                            }

                                                            if (total === 6) {
                                                              // Omitir posiciones 2, 3, 4 y 8 de 10
                                                              const faceIcons = [
                                                                'sentiment_very_dissatisfied',      // 1
                                                                'sentiment_dissatisfied',           // 5
                                                                'sentiment_neutral',                // 6
                                                                'sentiment_satisfied',              // 7
                                                                'sentiment_very_satisfied',         // 9
                                                                'sentiment_excited'                 // 10
                                                              ];
                                                              return getMaterialIcon(faceIcons[index] || faceIcons[0]);
                                                            }

                                                            if (total === 5) {
                                                              // Progresión equilibrada: muy triste, triste, neutral, feliz, muy feliz
                                                              const faceIcons = [
                                                                'sentiment_very_dissatisfied',      // 1
                                                                'sentiment_dissatisfied',           // 5
                                                                'sentiment_neutral',                // 6
                                                                'sentiment_satisfied',              // 7
                                                                'sentiment_very_satisfied'          // 9
                                                              ];
                                                              return getMaterialIcon(faceIcons[index] || faceIcons[0]);
                                                            }

                                                            if (total === 4) {
                                                              // Progresión básica: triste, neutral, feliz, muy feliz
                                                              const faceIcons = [
                                                                'sentiment_dissatisfied',           // 5
                                                                'sentiment_neutral',                // 6
                                                                'sentiment_satisfied',              // 7
                                                                'sentiment_very_satisfied'          // 9
                                                              ];
                                                              return getMaterialIcon(faceIcons[index] || faceIcons[0]);
                                                            }

                                                            if (total === 3) {
                                                              // Progresión mínima: triste, neutral, feliz
                                                              const faceIcons = [
                                                                'sentiment_dissatisfied',           // 5
                                                                'sentiment_neutral',                // 6
                                                                'sentiment_satisfied'               // 7
                                                              ];
                                                              return getMaterialIcon(faceIcons[index] || faceIcons[0]);
                                                            }

                                                            if (total === 2) {
                                                              // Progresión simple: triste, feliz
                                                              const faceIcons = [
                                                                'sentiment_dissatisfied',           // 5
                                                                'sentiment_satisfied'               // 7
                                                              ];
                                                              return getMaterialIcon(faceIcons[index] || faceIcons[0]);
                                                            }

                                                            // Fallback: usar iconos básicos según progreso
                                                            if (progress <= 0.33) {
                                                              return getMaterialIcon('sentiment_dissatisfied');
                                                            } else if (progress <= 0.66) {
                                                              return getMaterialIcon('sentiment_neutral');
                                                            } else {
                                                              return getMaterialIcon('sentiment_satisfied');
                                                            }
                                                          };

                                                          return getFaceIcon(idx, total);
                                                        })()}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              });
                                            })()}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    {element.type === 'slider' && (
                                      <div className="slider-multi-builder">
                                        {element.sliders.map((slider, idx) => (
                                          <div key={idx} className="slider-multi-row" style={{ marginBottom: 24 }}>
                                            <input
                                              type="text"
                                              value={slider.label}
                                              onChange={e => {
                                                const newSliders = [...element.sliders];
                                                if (newSliders[idx]) {
                                                  newSliders[idx].label = e.target.value;
                                                  updateQuestion(currentPage, elementIndex, 'sliders', newSliders);
                                                }
                                              }}
                                              className="slider-multi-label"
                                              placeholder={`Referencia ${idx + 1}`}
                                            />
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                                              <input
                                                type="range"
                                                min={slider.min}
                                                max={slider.max}
                                                value={slider.min}
                                                className="slider-preview-range"
                                                style={{ flex: 1 }}
                                                disabled
                                              />
                                              <span className="slider-value" style={{ minWidth: 32, textAlign: 'center', fontSize: 16 }}>{slider.min}</span>
                                              <button
                                                type="button"
                                                className="remove-btn"
                                                onClick={() => {
                                                  if (element.sliders.length > 1) {
                                                    const newSliders = element.sliders.filter((_, i) => i !== idx);
                                                    updateQuestion(currentPage, elementIndex, 'sliders', newSliders);
                                                  }
                                                }}
                                                disabled={element.sliders.length <= 1}
                                                style={{ marginLeft: 8, background: '#ff4d4f', color: '#fff', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, cursor: element.sliders.length > 1 ? 'pointer' : 'not-allowed', transition: 'background 0.2s' }}
                                                title="Eliminar slider"
                                              >
                                                ×
                                              </button>
                                            </div>
                                            <div className="slider-minmax-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                                              <input
                                                type="number"
                                                min={0}
                                                max={slider.max - 1}
                                                value={slider.min}
                                                onChange={e => {
                                                  let newMin = Number(e.target.value);
                                                  if (newMin < 0) newMin = 0;
                                                  if (newMin >= slider.max) newMin = slider.max - 1;
                                                  const newSliders = [...element.sliders];
                                                  if (newSliders[idx]) {
                                                    newSliders[idx].min = newMin;
                                                    // El valor del slider siempre será igual al min
                                                    newSliders[idx].value = newMin;
                                                    updateQuestion(currentPage, elementIndex, 'sliders', newSliders);
                                                  }
                                                }}
                                                className="slider-min-input"
                                              />
                                              <input
                                                type="number"
                                                min={slider.min + 1}
                                                max={100}
                                                value={slider.max}
                                                onChange={e => {
                                                  let newMax = Number(e.target.value);
                                                  if (newMax > 100) newMax = 100;
                                                  if (newMax <= slider.min) newMax = slider.min + 1;
                                                  const newSliders = [...element.sliders];
                                                  if (newSliders[idx]) {
                                                    newSliders[idx].max = newMax;
                                                    updateQuestion(currentPage, elementIndex, 'sliders', newSliders);
                                                  }
                                                }}
                                                className="slider-max-input"
                                              />
                                            </div>
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          className="add-slider-btn"
                                          onClick={() => {
                                            const newSliders = [...element.sliders];
                                            newSliders.push({ label: '', value: 0, min: 0, max: 100 });
                                            updateQuestion(currentPage, elementIndex, 'sliders', newSliders);
                                          }}
                                        >
                                          + Agregar slider
                                        </button>
                                      </div>
                                    )}
                                    {element.type === 'matrix' && (
                                      <div className="matrix-builder-container">
                                        <div className="matrix-preview-table-container">
                                          <table className="matrix-preview-table">
                                            <thead>
                                              <tr>
                                                <th className="matrix-corner"></th>
                                                {element.columns && element.columns.map((col, colIdx) => (
                                                  <th key={colIdx} className="matrix-col-header">
                                                    <div className="matrix-col-header-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                      <button
                                                        type="button"
                                                        className="matrix-remove-col-btn"
                                                        onClick={() => {
                                                          if (element.columns.length > 1) {
                                                            const newCols = element.columns.filter((_, i) => i !== colIdx);
                                                            updateQuestion(currentPage, elementIndex, 'columns', newCols);
                                                          }
                                                        }}
                                                        disabled={element.columns.length <= 1}
                                                        title="Eliminar columna"
                                                      >
                                                        ×
                                                      </button>
                                                      <textarea
                                                        value={col}
                                                        onChange={e => {
                                                          const newCols = [...element.columns];
                                                          newCols[colIdx] = e.target.value;
                                                          updateQuestion(currentPage, elementIndex, 'columns', newCols);
                                                        }}
                                                        className="matrix-col-input-inline"
                                                        placeholder={`Column ${colIdx + 1}`}
                                                        rows={2}
                                                        style={{ resize: 'none', minHeight: '40px' }}
                                                      />
                                                    </div>
                                                  </th>
                                                ))}
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {element.rows && element.rows.map((row, rowIdx) => (
                                                <tr key={rowIdx} className="matrix-preview-row">
                                                  <td className="matrix-row-label-cell">
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                      <button
                                                        type="button"
                                                        className="matrix-remove-row-btn"
                                                        onClick={() => {
                                                          if (element.rows.length > 1) {
                                                            const newRows = element.rows.filter((_, i) => i !== rowIdx);
                                                            updateQuestion(currentPage, elementIndex, 'rows', newRows);
                                                          }
                                                        }}
                                                        disabled={element.rows.length <= 1}
                                                        title="Eliminar fila"
                                                      >
                                                        ×
                                                      </button>
                                                      <textarea
                                                        value={row}
                                                        onChange={e => {
                                                          const newRows = [...element.rows];
                                                          newRows[rowIdx] = e.target.value;
                                                          updateQuestion(currentPage, elementIndex, 'rows', newRows);
                                                        }}
                                                        className="matrix-row-input-inline"
                                                        placeholder={`Row ${rowIdx + 1}`}
                                                        rows={2}
                                                        style={{ resize: 'none', minHeight: '40px' }}
                                                      />
                                                    </div>
                                                  </td>
                                                  {element.columns && element.columns.map((_, colIdx) => (
                                                    <td key={colIdx} className="matrix-preview-cell">
                                                      <input type="radio" disabled name={`matrix-preview-row-${elementIndex}-${rowIdx}`} />
                                                    </td>
                                                  ))}
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                        <div className="matrix-add-controls" >
                                          <button
                                            type="button"
                                            className="matrix-add-row-btn"
                                            onClick={() => {
                                              const newRows = [...(element.rows || [])];
                                              newRows.push("");
                                              updateQuestion(currentPage, elementIndex, 'rows', newRows);
                                            }}
                                          >
                                            + Agregar fila
                                          </button>
                                          <button
                                            type="button"
                                            className="matrix-add-col-btn"
                                            onClick={() => {
                                              const newCols = [...(element.columns || [])];
                                              newCols.push("");
                                              updateQuestion(currentPage, elementIndex, 'columns', newCols);
                                            }}
                                          >
                                            + Agregar columna
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {element.type === 'file_upload' && (
                                      <div className="file-upload-preview-box">
                                        <div className="file-upload-icon" style={{ fontSize: 20, color: '#19B394' }}>
                                          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M16 36H36C40.4183 36 44 32.4183 44 28C44 23.5817 40.4183 20 36 20C35.4477 20 35 19.5523 35 19C35 14.5817 31.4183 11 24 11C16.5817 11 13 14.5817 13 19C13 19.5523 12.5523 20 12 20C7.58172 20 4 23.5817 4 28C4 32.4183 7.58172 36 12 36Z" stroke="#19B394" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M24 28V18" stroke="#19B394" strokeWidth="2.5" strokeLinecap="round" />
                                            <path d="M21 21L24 18L27 21" stroke="#19B394" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        </div>
                                        <div className="file-upload-title" style={{ color: '#19B394' }}>Subir un Archivo</div>
                                        <div className="file-upload-subtitle">Arrastre y suelte archivos aquí</div>
                                      </div>
                                    )}
                                    <div className="question-actions">
                                      {element.type === 'scale' && (
                                        <div className="scale-type-group">
                                          <select
                                            value={element.scaleType || 'numbers'}
                                            onChange={(e) => updateQuestion(currentPage, elementIndex, 'scaleType', e.target.value)}
                                            className="scale-type-select"
                                          >
                                            <option value="numbers">Números</option>
                                            <option value="stars">Estrellas</option>
                                            <option value="faces">Caritas</option>
                                          </select>
                                        </div>
                                      )}
                                      <div className="action-buttons-group">
                                        <button
                                          className="action-btn"
                                          onClick={() => moveQuestionUp(currentPage, elementIndex)}
                                          disabled={elementIndex === 0}
                                          title="Subir"
                                          type="button"
                                        >
                                          <ArrowUp size={16} style={{ marginRight: 5, color: '#19B394' }} />
                                        </button>
                                        <button
                                          className="action-btn"
                                          onClick={() => moveQuestionDown(currentPage, elementIndex)}
                                          disabled={elementIndex === survey.pages[currentPage].elements.length - 1}
                                          title="Bajar"
                                          type="button"
                                        >
                                          <ArrowDown size={16} style={{ marginRight: 5, color: '#19B394' }} />
                                        </button>
                                        <button
                                          className="action-btn"
                                          onClick={() => duplicateQuestion(currentPage, elementIndex)}
                                          title="Duplicar"
                                          type="button"
                                        >
                                          <Copy size={16} style={{ marginRight: 5, color: '#19B394' }} />
                                        </button>
                                        <button
                                          className={`action-btn ${element.required ? 'active' : ''}`}
                                          onClick={() => updateQuestion(currentPage, elementIndex, 'required', !element.required)}
                                          title="Requerido"
                                          type="button"
                                          style={{ color: element.required ? '#19B394' : undefined }}
                                        >
                                          <CheckSquare size={16} style={{ marginRight: 5, color: '#19B394' }} />
                                          {element.required ? <span style={{ color: '#19B394' }}>Requerido</span> : ''}
                                        </button>
                                        <button
                                          className="action-btn"
                                          onClick={() => removeQuestion(currentPage, elementIndex)}
                                          title="Eliminar"
                                          type="button"
                                        >
                                          <Trash2 size={16} style={{ marginRight: 5, color: '#19B394' }} />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  </div>
                  <div style={{ textAlign: 'center', marginTop: '30px' }}>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                      style={{ padding: '12px 30px', fontSize: '16px' }}
                    >
                      <Save size={16} style={{ marginRight: '8px' }} />
                      {loading ? 'Guardando...' : (editId ? 'Actualizar encuesta' : 'Crear encuesta')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="survey-right">
              <div className="conditions-block">
                <div className="conditions-title">Condiciones</div>
                <div className="conditions-fields">
                  <div className="toggle-row">
                    <span className="toggle-label">Fecha y hora</span>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={enableDates} onChange={e => setEnableDates(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>
                  {enableDates && (
                    <>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label className="conditions-label" style={{ fontWeight: 400 }}>Habilitar desde</label>
                        <input
                          type="date"
                          className="conditions-input"
                          value={survey.startDateOnly || ''}
                          onChange={e => setSurvey(prev => ({ ...prev, startDateOnly: e.target.value }))}
                        />
                        <input
                          type="time"
                          className="conditions-input"
                          value={survey.startTimeOnly || ''}
                          onChange={e => setSurvey(prev => ({ ...prev, startTimeOnly: e.target.value }))}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label className="conditions-label" style={{ fontWeight: 400 }}>Deshabilitar desde</label>
                        <input
                          type="date"
                          className="conditions-input"
                          value={survey.endDateOnly || ''}
                          onChange={e => setSurvey(prev => ({ ...prev, endDateOnly: e.target.value }))}
                        />
                        <input
                          type="time"
                          className="conditions-input"
                          value={survey.endTimeOnly || ''}
                          onChange={e => setSurvey(prev => ({ ...prev, endTimeOnly: e.target.value }))}
                        />
                      </div>
                    </>
                  )}
                  <div className="toggle-row" style={{ marginTop: '18px' }}>
                    <span className="toggle-label">Cantidad máxima de respuestas</span>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={enableMaxResponses} onChange={e => setEnableMaxResponses(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>
                  {enableMaxResponses && (
                    <input
                      type="number"
                      min="1"
                      className="conditions-input"
                      value={survey.maxResponses || ''}
                      onChange={e => setSurvey(prev => ({ ...prev, maxResponses: e.target.value }))}
                      placeholder="Sin límite"
                    />
                  )}
                  <div style={{ marginTop: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontWeight: 400, fontSize: '16px', color: '#222', fontFamily: 'inherit' }}>Tiempo</span>
                      <label className="toggle-switch">
                        <input type="checkbox" checked={enableTimeLimit} onChange={e => setEnableTimeLimit(e.target.checked)} />
                        <span className="slider"></span>
                      </label>
                    </div>
                    {enableTimeLimit && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <input
                          type="time"
                          step="1"
                          className="conditions-input"
                          value={survey.timeLimit || '00:00:00'}
                          onChange={e => setSurvey(prev => ({ ...prev, timeLimit: e.target.value }))}
                          placeholder="HH:MM:SS"
                        />
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontWeight: 400, fontSize: '16px', color: '#222', fontFamily: 'inherit' }}>Desempeño</span>
                      <label className="toggle-switch">
                        <input type="checkbox" checked={enablePerformance} onChange={e => setEnablePerformance(e.target.checked)} />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}
        {activeTab === 'configuracion' && (
          <div className="survey-configuración" >
            <div className="conditions-block">
              <div className="conditions-title">Condiciones</div>
              <div className="conditions-fields">
                <div className="toggle-row">
                  <span className="toggle-label">Fecha y hora</span>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={enableDates} onChange={e => setEnableDates(e.target.checked)} />
                    <span className="slider"></span>
                  </label>
                </div>
                {enableDates && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label className="conditions-label" style={{ fontWeight: 400 }}>Habilitar desde</label>
                      <input
                        type="date"
                        className="conditions-input"
                        value={survey.startDateOnly || ''}
                        onChange={e => setSurvey(prev => ({ ...prev, startDateOnly: e.target.value }))}
                      />
                      <input
                        type="time"
                        className="conditions-input"
                        value={survey.startTimeOnly || ''}
                        onChange={e => setSurvey(prev => ({ ...prev, startTimeOnly: e.target.value }))}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label className="conditions-label" style={{ fontWeight: 400 }}>Deshabilitar desde</label>
                      <input
                        type="date"
                        className="conditions-input"
                        value={survey.endDateOnly || ''}
                        onChange={e => setSurvey(prev => ({ ...prev, endDateOnly: e.target.value }))}
                      />
                      <input
                        type="time"
                        className="conditions-input"
                        value={survey.endTimeOnly || ''}
                        onChange={e => setSurvey(prev => ({ ...prev, endTimeOnly: e.target.value }))}
                      />
                    </div>
                  </>
                )}
                <div className="toggle-row" style={{ marginTop: '18px' }}>
                  <span className="toggle-label">Cantidad máxima de respuestas</span>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={enableMaxResponses} onChange={e => setEnableMaxResponses(e.target.checked)} />
                    <span className="slider"></span>
                  </label>
                </div>
                {enableMaxResponses && (
                  <input
                    type="number"
                    min="1"
                    className="conditions-input"
                    value={survey.maxResponses || ''}
                    onChange={e => setSurvey(prev => ({ ...prev, maxResponses: e.target.value }))}
                    placeholder="Sin límite"
                  />
                )}
                <div style={{ marginTop: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontWeight: 400, fontSize: '16px', color: '#222', fontFamily: 'inherit' }}>Tiempo</span>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={enableTimeLimit} onChange={e => setEnableTimeLimit(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>
                  {enableTimeLimit && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <input
                        type="time"
                        step="1"
                        className="conditions-input"
                        value={survey.timeLimit || '00:00:00'}
                        onChange={e => setSurvey(prev => ({ ...prev, timeLimit: e.target.value }))}
                        placeholder="HH:MM:SS"
                      />
                    </div>
                  )}
                </div>
                <div style={{ marginTop: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontWeight: 400, fontSize: '16px', color: '#222', fontFamily: 'inherit' }}>Desempeño</span>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={enablePerformance} onChange={e => setEnablePerformance(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'compartir' && (
          <div className="survey-compartir" style={{
            padding: '20px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: '100vh',
            width: '100%',
            transition: 'background 0.4s'
          }}>

            {!enablePerformance ? (
              // Vista normal para encuestas de bienestar
              <>
                <div className="send-survey-btn-container">
                  <h2 className="send-survey-btn-title">Compartir Encuesta</h2>
                  <button className="send-survey-btn" onClick={handleSendSurvey} disabled={sending} style={{ minWidth: 160, height: 40, marginLeft: 0, marginTop: 0 }}>{sending ? 'Enviando...' : 'Enviar encuesta'}</button>
                </div>
                {sendResult && (
                  <div className={`send-result-toast ${sendResult.success ? 'send-result-toast-success' : 'send-result-toast-error'}`}>
                    <span className="send-result-toast-icon">
                      {sendResult.success ? '✔️' : '❌'}
                    </span>
                    {sendResult.message}
                  </div>
                )}
                <div style={{
                  background: '#fff',
                  borderRadius: 16,
                  boxShadow: '0 2px 12px rgba(25,179,148,0.08)',
                  padding: '32px 0',
                  width: '98%',
                  maxWidth: 'none',
                  margin: '0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}>
                  <PersonCompaniesTable
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    handleCheckboxChange={handleCheckboxChange}
                    enviadosEmail={enviadosEmail}
                    setShared={setShared}
                    title="Selecciona los destinatarios"
                    enablePerformance={enablePerformance}
                  />
                </div>
              </>
            ) : (
              // Vista para evaluación de desempeño
              <PerformanceEvaluationView
                surveyId={survey._id || survey.id || editId}
                enablePerformance={enablePerformance}
                onEvaluationConfigured={() => setRefreshEvaluations(prev => prev + 1)}
              />
            )}
          </div>
        )}
        {activeTab === 'compartidos' && (
          <div className='compartidos-container'>
            <div className='compartidos-title-container' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <h2 className="compartidos-title">
                {!enablePerformance ? 'Compartidos' : 'Evaluaciones de Desempeño'}
              </h2>
              {enablePerformance ? (
                <button
                  onClick={() => window.dispatchEvent(new Event('pe-export-excel'))}
                  title="Exportar a Excel"
                  aria-label="Exportar a Excel"
                  className="excel-btn"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    outline: 'none',
                    transition: 'transform 0.12s ease-in-out'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.06)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                  <img
                    src={process.env.PUBLIC_URL + '/icons/excel.png'}
                    alt="Excel"
                    style={{ width: 40, height: 40 }}
                    referrerPolicy="no-referrer"
                  />
                </button>
              ) : (
                <button
                  onClick={() => window.dispatchEvent(new Event('compartidos-export-excel'))}
                  title="Exportar a Excel"
                  aria-label="Exportar a Excel"
                  className="excel-btn"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    outline: 'none',
                    transition: 'transform 0.12s ease-in-out'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.06)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                  <img
                    src={process.env.PUBLIC_URL + '/icons/excel.png'}
                    alt="Excel"
                    style={{ width: 40, height: 40 }}
                    referrerPolicy="no-referrer"
                  />
                </button>
              )}
            </div>
            <div className="compartidos-table-container">
              {loadingShared ? (
                <div>Cargando...</div>
              ) : !enablePerformance ? (
                // Vista normal para encuestas de bienestar
                <CompartidosTable shared={shared} onDeleteCompartido={handleDeleteCompartido} />
              ) : (
                // Vista para evaluaciones de desempeño
                <PerformanceEvaluationsTable
                  surveyId={survey._id || survey.id || editId}
                  enablePerformance={enablePerformance}
                  surveyTitle={survey.title}
                  refreshTrigger={refreshEvaluations}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Tabla que consume la API y mapea los datos según el ejemplo
const PersonCompaniesTable = ({ selectedRows = [], setSelectedRows = () => { }, handleCheckboxChange = () => { }, enviadosEmail = new Set(), setShared = () => { }, title, enablePerformance = false, disabledDniSet = new Set() }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Paginación y filtro
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const tenants = Array.from({ length: 14 }, (_, i) => (i + 1).toString().padStart(2, '0'));
    const apiKey = 'GpGateway-OFiplan-2024-KEY-1234567890';
    const url = 'https://intranet.grupopolo.pe/Gp-Api-OFiplan/api/v1/api/PersonCompanies';
    setLoading(true);
    Promise.all(
      tenants.map(tenant =>
        axios.get(url, {
          headers: {
            'X-Tenant-Id': tenant,
            'x-api-key': apiKey
          }
        })
          .then(res => (res.data.value || []).map(item => ({ ...item, tenant })))
          .catch(() => [])
      )
    )
      .then(results => {
        setData(results.flat());
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Wrapper para handleCheckboxChange que incluye enablePerformance
  const handleCheckboxChangeWithPerformance = (row, checked) => {
    // Función para obtener el email correcto según el toggle de desempeño
    const getEmailForSelection = (row) => {
      if (enablePerformance) {
        // Toggle de desempeño activado: usar emailTwo si existe, sino email
        return row.person?.emailTwo || row.person?.email || '';
      } else {
        // Toggle de desempeño desactivado: usar email principal
        return row.person?.email || '';
      }
    };

    // Crear un row normalizado con el email correcto
    const normalizedRow = {
      ...row,
      person: {
        ...row.person,
        email: getEmailForSelection(row)
      }
    };

    handleCheckboxChange(normalizedRow, checked);
  };

  // Obtengo opciones únicas para los selects
  const unique = (arr) => Array.from(new Set(arr.filter(Boolean)));
  const empresas = unique(data.map(item => item.company?.name));
  const sedes = unique(data.map(item => item.sede?.name));
  const departamentos = unique(data.map(item => item.department?.name));
  const areas = unique(data.map(item => item.area?.name));
  const secciones = unique(data.map(item => item.section?.name));
  const ocupaciones = unique(data.map(item => item.occupationGroup?.name));
  const puestosTrabajo = unique(data.map(item => item.jobPosition?.name));

  // Estado de los selects
  const [empresaSel, setEmpresaSel] = useState('');
  const [sedeSel, setSedeSel] = useState('');
  const [departamentoSel, setDepartamentoSel] = useState('');
  const [areaSel, setAreaSel] = useState('');
  const [seccionSel, setSeccionSel] = useState('');
  const [ocupacionSel, setOcupacionSel] = useState('');
  const [puestoSel, setPuestoSel] = useState('');

  // Función para obtener el email correcto según el toggle de desempeño
  const getEmailForDisplay = (item) => {
    if (enablePerformance) {
      // Desempeño: mostrar emailTwo si existe
      return item.person?.emailTwo || item.person?.email || '';
    } else {
      // Bienestar: mostrar email principal
      return item.person?.email || '';
    }
  };

  // Función para obtener el email correcto para la lógica de selección
  const getEmailForLogic = (item) => {
    if (enablePerformance) {
      // Desempeño: usar emailTwo si existe
      return item.person?.emailTwo || item.person?.email || '';
    } else {
      // Bienestar: usar email principal
      return item.person?.email || '';
    }
  };

  // Filtro por todos los campos visibles en la tabla y selects
  const filteredData = data.filter(item => {
    const values = [
      item.company?.name,
      item.sede?.name,
      item.department?.name,
      item.area?.name,
      item.section?.name,
      item.occupationGroup?.name,
      item.jobPosition?.name,
      item.person?.name,
      item.person?.id,
      getEmailForDisplay(item)
    ];
    const matchesSelects =
      (!empresaSel || item.company?.name === empresaSel) &&
      (!sedeSel || item.sede?.name === sedeSel) &&
      (!departamentoSel || item.department?.name === departamentoSel) &&
      (!areaSel || item.area?.name === areaSel) &&
      (!seccionSel || item.section?.name === seccionSel) &&
      (!ocupacionSel || item.occupationGroup?.name === ocupacionSel) &&
      (!puestoSel || item.jobPosition?.name === puestoSel);
    return matchesSelects && values.some(val => (val || '').toLowerCase().includes(filter.toLowerCase()));
  });

  // Paginación
  const totalPages = Math.ceil((filteredData?.length || 0) / rowsPerPage);
  const paginatedData = filteredData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handlePrev = () => setPage(p => Math.max(1, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages, p + 1));
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  // Si cambias el filtro, vuelve a la página 1
  useEffect(() => { setPage(1); }, [filter]);

  if (loading) return <div style={{ color: '#19B394', fontWeight: 500, fontSize: 18 }}>Cargando datos...</div>;

  return (
    <div style={{ width: '98%' }}>
      {title && (
        <div style={{ marginBottom: '16px', textAlign: 'center' }}>
          <h3 style={{ color: '#19B394', fontWeight: 600, fontSize: '18px', margin: 0 }}>{title}</h3>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'row', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <select value={empresaSel} onChange={e => setEmpresaSel(e.target.value)} style={{ minWidth: 140, padding: 8, borderRadius: 8, border: '1.5px solid #19B394', fontSize: 15, background: '#F6FCFB', color: '#222', outline: 'none', boxShadow: '0 1px 4px rgba(25,179,148,0.06)' }}>
          <option value=''>Empresa</option>
          {empresas.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <select value={sedeSel} onChange={e => setSedeSel(e.target.value)} style={{ minWidth: 140, padding: 8, borderRadius: 8, border: '1.5px solid #19B394', fontSize: 15, background: '#F6FCFB', color: '#222', outline: 'none', boxShadow: '0 1px 4px rgba(25,179,148,0.06)' }}>
          <option value=''>Sede</option>
          {sedes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <select value={departamentoSel} onChange={e => setDepartamentoSel(e.target.value)} style={{ minWidth: 140, padding: 8, borderRadius: 8, border: '1.5px solid #19B394', fontSize: 15, background: '#F6FCFB', color: '#222', outline: 'none', boxShadow: '0 1px 4px rgba(25,179,148,0.06)' }}>
          <option value=''>Departamento</option>
          {departamentos.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <select value={areaSel} onChange={e => setAreaSel(e.target.value)} style={{ minWidth: 140, padding: 8, borderRadius: 8, border: '1.5px solid #19B394', fontSize: 15, background: '#F6FCFB', color: '#222', outline: 'none', boxShadow: '0 1px 4px rgba(25,179,148,0.06)' }}>
          <option value=''>Área</option>
          {areas.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <select value={seccionSel} onChange={e => setSeccionSel(e.target.value)} style={{ minWidth: 140, padding: 8, borderRadius: 8, border: '1.5px solid #19B394', fontSize: 15, background: '#F6FCFB', color: '#222', outline: 'none', boxShadow: '0 1px 4px rgba(25,179,148,0.06)' }}>
          <option value=''>Sección</option>
          {secciones.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <select value={ocupacionSel} onChange={e => setOcupacionSel(e.target.value)} style={{ minWidth: 140, padding: 8, borderRadius: 8, border: '1.5px solid #19B394', fontSize: 15, background: '#F6FCFB', color: '#222', outline: 'none', boxShadow: '0 1px 4px rgba(25,179,148,0.06)' }}>
          <option value=''>Ocupación</option>
          {ocupaciones.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <select value={puestoSel} onChange={e => setPuestoSel(e.target.value)} style={{ minWidth: 160, padding: 8, borderRadius: 8, border: '1.5px solid #19B394', fontSize: 15, background: '#F6FCFB', color: '#222', outline: 'none', boxShadow: '0 1px 4px rgba(25,179,148,0.06)' }}>
          <option value=''>Puesto de Trabajo</option>
          {puestosTrabajo.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Buscar por nombre, DNI, correo, ocupación o puesto..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{
            padding: 8,
            width: 320,
            border: '1px solid #19B394',
            borderRadius: 6,
            fontSize: 16
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#19B394', fontWeight: 500 }}>Filas por página:</span>
          <select value={rowsPerPage} onChange={handleRowsPerPageChange} style={{ padding: 6, borderRadius: 6, border: '1px solid #19B394', fontSize: 16 }}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </div>
      </div>
      <div style={{ overflowX: 'auto', borderRadius: 12, boxShadow: '0 2px 8px rgba(25,179,148,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
          <thead>
            <tr style={{ background: '#EDF9F7', borderBottom: '2px solid #19B394' }}>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center', borderTopLeftRadius: 8 }}>N°</th>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center' }}>Empresa</th>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center' }}>Sede</th>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center' }}>Departamento</th>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center' }}>Area</th>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center' }}>Sección</th>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center' }}>Ocupación</th>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center' }}>Puesto de Trabajo</th>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center' }}>Nombres y apellidos</th>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center' }}>DNI</th>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center', borderTopRightRadius: 8 }}>Correo</th>
              <th style={{ color: '#19B394', fontWeight: 600, padding: '12px 8px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={paginatedData
                    .filter(item => !enviadosEmail.has(normEmail(getEmailForLogic(item))))
                    .filter(item => !disabledDniSet.has(String(item.person?.id)))
                    .every(item => selectedRows.some(r => String(r.dni) === String(item.person?.id))) && paginatedData.filter(item => !enviadosEmail.has(getEmailForLogic(item)) && !disabledDniSet.has(String(item.person?.id))).length > 0}
                  onChange={e => {
                    if (e.target.checked) {
                      // Selecciona todos los visibles que NO están en enviadosEmail ni deshabilitados
                      const nuevos = paginatedData
                        .filter(item => !enviadosEmail.has(normEmail(getEmailForLogic(item))))
                        .filter(item => !disabledDniSet.has(String(item.person?.id)))
                        .filter(item => !selectedRows.some(r => String(r.dni) === String(item.person?.id)));

                      // Usar handleCheckboxChange para cada item para mantener consistencia
                      nuevos.forEach(item => {
                        handleCheckboxChangeWithPerformance(item, true);
                      });
                    } else {
                      // Deselecciona todos los visibles (solo los de la página actual) que no estén deshabilitados
                      const aDeseleccionar = paginatedData
                        .filter(item => !enviadosEmail.has(normEmail(getEmailForLogic(item))))
                        .filter(item => !disabledDniSet.has(String(item.person?.id)))
                        .filter(item => selectedRows.some(r => String(r.dni) === String(item.person?.id)));

                      // Usar handleCheckboxChange para cada item para mantener consistencia
                      aDeseleccionar.forEach(item => {
                        handleCheckboxChangeWithPerformance(item, false);
                      });
                    }
                  }}
                  style={{ width: 18, height: 18, accentColor: '#19B394', border: '2px solid #19B394', borderRadius: 4, cursor: 'pointer' }}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {(paginatedData?.length || 0) === 0 ? (
              <tr><td colSpan={12} style={{ textAlign: 'center', padding: 24, color: '#888', fontSize: 13 }}>Sin datos</td></tr>
            ) : (
              paginatedData.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0', background: (idx % 2 === 0) ? '#fff' : '#F6FCFB', fontSize: 13 }}>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{(page - 1) * rowsPerPage + idx + 1}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.company?.name || ''}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.sede?.name || ''}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.department?.name || ''}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.area?.name || ''}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.section?.name || ''}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.occupationGroup?.name || ''}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.jobPosition?.name || ''}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.person?.name || ''}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.person?.id || ''}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>{getEmailForDisplay(item)}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                    {disabledDniSet.has(String(item.person?.id)) ? (
                      <input
                        type="checkbox"
                        checked={true}
                        disabled
                        title="Ya asignado para este evaluador en esta encuesta"
                        style={{ width: 18, height: 18, accentColor: '#19B394', border: '2px solid #19B394', borderRadius: 4, transition: 'background 0.2s', background: '#fff', cursor: 'not-allowed' }}
                      />
                    ) : (enviadosEmail.has(normEmail(getEmailForLogic(item)))) ? (
                      <input
                        type="checkbox"
                        checked={true}
                        disabled
                        style={{ width: 18, height: 18, accentColor: '#19B394', border: '2px solid #19B394', borderRadius: 4, transition: 'background 0.2s', background: '#fff', cursor: 'not-allowed' }}
                      />
                    ) : (
                      <input
                        type="checkbox"
                        checked={selectedRows.some(r => String(r.dni) === String(item.person?.id))}
                        onChange={e => handleCheckboxChangeWithPerformance(
                          item,
                          e.target.checked
                        )}
                        style={{ width: 18, height: 18, accentColor: '#19B394', border: '2px solid #19B394', borderRadius: 4, transition: 'background 0.2s', background: '#fff', cursor: 'pointer' }}
                      />
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Controles de paginación */}
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16 }}>
        <button onClick={handlePrev} disabled={page === 1} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #19B394', background: page === 1 ? '#eee' : '#19B394', color: page === 1 ? '#888' : '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>Anterior</button>
        <span style={{ fontWeight: 500, color: '#19B394' }}>Página {page} de {totalPages}</span>
        <button onClick={handleNext} disabled={page === totalPages || totalPages === 0} style={{ padding: 8, borderRadius: 6, border: '1px solid #19B394', background: page === totalPages || totalPages === 0 ? '#eee' : '#19B394', color: page === totalPages || totalPages === 0 ? '#888' : '#fff', cursor: page === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer' }}>Siguiente</button>
      </div>
    </div>
  );
};

// Nuevo componente para la tabla de compartidos con filtros
const CompartidosTable = ({ shared, onDeleteCompartido }) => {
  const [empresaFilter, setEmpresaFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');

  // Obtener opciones únicas para los filtros y ordenarlas alfabéticamente
  const empresas = [...new Set(shared.map(item => item.company).filter(Boolean))].sort();
  const areas = [...new Set(shared.map(item => item.area).filter(Boolean))].sort();

  // Filtrar datos
  const filteredShared = shared.filter(item => {
    const matchesEmpresa = !empresaFilter || item.company === empresaFilter;
    const matchesArea = !areaFilter || item.area === areaFilter;
    const matchesEstado = !estadoFilter || (item.estado || 'Incompleto') === estadoFilter;
    return matchesEmpresa && matchesArea && matchesEstado;
  });

  return (
    <div>
      {/* Filtros */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select
          value={empresaFilter}
          onChange={e => setEmpresaFilter(e.target.value)}
          style={{
            minWidth: '140px',
            padding: '8px',
            borderRadius: '8px',
            border: '1.5px solid #19B394',
            fontSize: '15px',
            background: '#F6FCFB',
            color: '#222',
            outline: 'none',
            boxShadow: '0 1px 4px rgba(25,179,148,0.06)'
          }}
        >
          <option value=''>Todas las empresas</option>
          {empresas.map(empresa => (
            <option key={empresa} value={empresa}>{empresa}</option>
          ))}
        </select>
        <select
          value={areaFilter}
          onChange={e => setAreaFilter(e.target.value)}
          style={{
            minWidth: '140px',
            padding: '8px',
            borderRadius: '8px',
            border: '1.5px solid #19B394',
            fontSize: '15px',
            background: '#F6FCFB',
            color: '#222',
            outline: 'none',
            boxShadow: '0 1px 4px rgba(25,179,148,0.06)'
          }}
        >
          <option value=''>Todas las áreas</option>
          {areas.map(area => (
            <option key={area} value={area}>{area}</option>
          ))}
        </select>
        <select
          value={estadoFilter}
          onChange={e => setEstadoFilter(e.target.value)}
          style={{
            minWidth: '140px',
            padding: '8px',
            borderRadius: '8px',
            border: '1.5px solid #19B394',
            fontSize: '15px',
            background: '#F6FCFB',
            color: '#222',
            outline: 'none',
            boxShadow: '0 1px 4px rgba(25,179,148,0.06)'
          }}
        >
          <option value=''>Todos los estados</option>
          <option value='Completado'>Completado</option>
          <option value='En Curso'>En Curso</option>
          <option value='Incompleto'>Incompleto</option>
        </select>
      </div>

      {/* Contador de registros */}
      <div style={{ 
        marginBottom: '16px',
        textAlign: 'right',
        fontSize: '14px',
        color: '#666',
        fontWeight: '500'
      }}>
        Mostrando {filteredShared.length} de {shared.length} registros
        {(empresaFilter || areaFilter || estadoFilter) && (
          <span style={{ marginLeft: '8px' }}>
            (filtrado)
          </span>
        )}
      </div>

      {/* Tabla */}
      <table className="compartidos-table">
        <thead>
          <tr>
            <th style={{ textAlign: 'center' }}>Empresa</th>
            <th style={{ textAlign: 'center' }}>Área</th>
            <th style={{ textAlign: 'center' }}>Nombre</th>
            <th style={{ textAlign: 'center' }}>DNI</th>
            <th style={{ textAlign: 'center' }}>Correo</th>
            <th style={{ textAlign: 'center' }}>Estado</th>
            <th style={{ textAlign: 'center' }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredShared.map((row, idx) => (
            <tr key={row._id || idx}>
              <td style={{ textAlign: 'center' }}>{row.company || ''}</td>
              <td style={{ textAlign: 'center' }}>{row.area || ''}</td>
              <td style={{ textAlign: 'center' }}>{row.name}</td>
              <td style={{ textAlign: 'center' }}>{row.dni}</td>
              <td style={{ textAlign: 'center' }}>{row.email}</td>
              <td style={{ textAlign: 'center', ...estadoColor(row.estado) }}>{row.estado || 'Incompleto'}</td>
              <td style={{ textAlign: 'center' }}>
                <button onClick={() => onDeleteCompartido(row._id)} style={{ color: '#dc3545', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Eliminar</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Componente para evaluación de desempeño
const PerformanceEvaluationView = ({ surveyId, enablePerformance, onEvaluationConfigured }) => {
  const [step, setStep] = useState('evaluators'); // 'evaluators' o 'evaluatees'
  const [selectedEvaluators, setSelectedEvaluators] = useState([]);
  // selectedEvaluatees representa los evaluados seleccionados PARA EL EVALUADOR ACTUAL
  const [selectedEvaluatees, setSelectedEvaluatees] = useState([]);
  // assignments: { [evaluatorDni]: Array<evaluateeObj> }
  const [assignments, setAssignments] = useState({});
  const [currentEvaluatorDni, setCurrentEvaluatorDni] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // Map de evaluados ya asignados por evaluador (según BD) para esta encuesta
  const [alreadyAssignedMap, setAlreadyAssignedMap] = useState({}); // { [evaluatorDni]: Set<evaluateeDni> }

  const handleSetupEvaluators = () => {
    if (selectedEvaluators.length === 0) {
      toast.error('Debe seleccionar al menos un evaluador');
      return;
    }
    // Inicializar evaluador actual al primero seleccionado
    const first = selectedEvaluators[0];
    setCurrentEvaluatorDni(first?.dni || String(first?.id || ''));
    setAssignments(prev => ({ ...prev }));
    // Mostrar los evaluados del evaluador actual si existieran en assignments
    const dni = first?.dni || String(first?.id || '');
    const existing = (dni && assignments[dni]) ? assignments[dni] : [];
    setSelectedEvaluatees(existing);
    setStep('evaluatees');
  };

  // Cargar desde BD los evaluados ya asignados por evaluador cuando entremos a 'evaluatees' o cambie el evaluador
  useEffect(() => {
    const loadAlreadyAssigned = async () => {
      if (!surveyId || step !== 'evaluatees') return;
      try {
        const resp = await api.get(`/api/performance-evaluation/${surveyId}`);
        const data = resp.data || {};
        const map = {};
        for (const p of (data.evaluations || [])) {
          const ev = String(p.evaluatorId);
          const ee = String(p.evaluateeId);
          if (!map[ev]) map[ev] = new Set();
          map[ev].add(ee);
        }
        setAlreadyAssignedMap(map);
      } catch (_) {
        setAlreadyAssignedMap({});
      }
    };
    loadAlreadyAssigned();
  }, [surveyId, step, currentEvaluatorDni]);

  const handleConfirmEvaluation = async () => {
    // Asegurar que el evaluador actual tenga sus selecciones reflejadas
    const mergedAssignments = {
      ...assignments,
      ...(currentEvaluatorDni ? { [String(currentEvaluatorDni)]: selectedEvaluatees } : {}),
    };

    // Construir pairs desde assignments
    const allPairs = [];
    const allEvaluateesMap = new Map();
    Object.entries(mergedAssignments).forEach(([evDni, list]) => {
      (list || []).forEach(ee => {
        if (!ee || !ee.dni) return;
        allPairs.push({ evaluatorId: String(evDni), evaluateeId: String(ee.dni) });
        if (!allEvaluateesMap.has(String(ee.dni))) {
          allEvaluateesMap.set(String(ee.dni), ee);
        }
      });
    });
    // Debug ligero: conteo por evaluador
    if (process.env.NODE_ENV !== 'production') {
      const counts = Object.fromEntries(Object.entries(mergedAssignments).map(([k, v]) => [k, (v || []).length]));
      // eslint-disable-next-line no-console
      console.log('Asignaciones por evaluador (conteo):', counts);
    }

    if (allPairs.length === 0) {
      toast.error('Debe asignar al menos un evaluado a algún evaluador');
      return;
    }

    // Evaluados únicos usados en las asignaciones
    const uniqueEvaluatees = Array.from(allEvaluateesMap.values());

    setLoading(true);
    try {
      // Enviar configuración con pairs explícitos
      await api.post('/api/performance-evaluation/setup', {
        surveyId,
        evaluators: selectedEvaluators,
        evaluatees: uniqueEvaluatees,
        pairs: allPairs,
      });

      // Iniciar evaluación
      const response = await api.post('/api/performance-evaluation/start', { surveyId });
      setResult(response.data);
      toast.success('Evaluación configurada y emails enviados correctamente');

      if (onEvaluationConfigured) onEvaluationConfigured();

      // Reset UI
      setTimeout(() => {
        setSelectedEvaluators([]);
        setSelectedEvaluatees([]);
        setAssignments({});
        setCurrentEvaluatorDni(null);
        setStep('evaluators');
        setResult(null);
      }, 3000);
    } catch (error) {
      toast.error('Error al configurar la evaluación');
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluatorSelection = (row, checked) => {
    // Función para obtener el email correcto según el toggle de desempeño
    const getEmailForSelection = (row) => {
      if (enablePerformance) {
        return row.person?.emailTwo || row.person?.email || '';
      } else {
        return row.person?.email || '';
      }
    };

    if (checked) {
      setSelectedEvaluators(prev => [...prev, {
        id: row.person?.id,
        name: row.person?.name,
        email: getEmailForSelection(row),
        dni: row.person?.id,
        company: row.company?.name,
        sede: row.sede?.name,
        department: row.department?.name,
        area: row.area?.name,
        section: row.section?.name,
        jobPosition: row.jobPosition?.name,
        companyEntryDate: row.companyEntryDate
      }]);
    } else {
      // Quitar por DNI para evitar conflictos cuando varios comparten el mismo correo
      setSelectedEvaluators(prev => prev.filter(r => String(r.dni) !== String(row.person?.id)));
    }
  };

  const handleEvaluateeSelection = (row, checked) => {
    // Función para obtener el email correcto según el toggle de desempeño
    const getEmailForSelection = (row) => {
      if (enablePerformance) {
        return row.person?.emailTwo || row.person?.email || '';
      } else {
        return row.person?.email || '';
      }
    };

    if (!currentEvaluatorDni) return;

    const eeObj = {
      id: row.person?.id,
      name: row.person?.name,
      email: getEmailForSelection(row),
      dni: row.person?.id,
      company: row.company?.name,
      sede: row.sede?.name,
      department: row.department?.name,
      area: row.area?.name,
      section: row.section?.name,
      jobPosition: row.jobPosition?.name,
      companyEntryDate: row.companyEntryDate
    };

    // Bloquear si ya está asignado en BD para este evaluador (por esta encuesta)
    const isAlreadyAssigned = currentEvaluatorDni && alreadyAssignedMap[String(currentEvaluatorDni)] && alreadyAssignedMap[String(currentEvaluatorDni)].has(String(eeObj.dni));
    if (isAlreadyAssigned) {
      toast.error('Este evaluado ya está asignado a este evaluador en esta encuesta.');
      return;
    }

    setAssignments(prev => {
      const current = Array.isArray(prev[currentEvaluatorDni]) ? [...prev[currentEvaluatorDni]] : [];
      if (checked) {
        if (!current.some(e => String(e.dni) === String(eeObj.dni))) current.push(eeObj);
      } else {
        const idx = current.findIndex(e => String(e.dni) === String(eeObj.dni));
        if (idx >= 0) current.splice(idx, 1);
      }
      const next = { ...prev, [currentEvaluatorDni]: current };
      // Mantener selectedEvaluatees sincronizado con el evaluador actual
      setSelectedEvaluatees(current);
      return next;
    });
  };

  const handleChangeCurrentEvaluator = (dni) => {
    // Guardar el estado actual del evaluador anterior antes de cambiar
    setAssignments(prev => ({
      ...prev,
      ...(currentEvaluatorDni ? { [String(currentEvaluatorDni)]: selectedEvaluatees } : {}),
    }));
    setCurrentEvaluatorDni(dni);
    const list = (assignments && assignments[dni]) ? assignments[dni] : [];
    setSelectedEvaluatees(list);
  };

  // Sincronizar assignments cuando cambien los seleccionados del evaluador actual (por acciones masivas del grid)
  useEffect(() => {
    if (!currentEvaluatorDni) return;
    setAssignments(prev => ({ ...prev, [String(currentEvaluatorDni)]: selectedEvaluatees }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvaluatees, currentEvaluatorDni]);

  return (
    <>
      <div className="send-survey-btn-container">
        <h2 className="send-survey-btn-title">
          {step === 'evaluators' ? 'Escoge Evaluador' : 'Escoge Evaluados'}
        </h2>
        <button
          className="send-survey-btn"
          onClick={step === 'evaluators' ? handleSetupEvaluators : handleConfirmEvaluation}
          disabled={loading}
          style={{ minWidth: 160, height: 40, marginLeft: 0, marginTop: 0 }}
        >
          {loading ? 'Enviando...' : (step === 'evaluators' ? 'Siguiente' : 'Confirmar')}
        </button>
      </div>

      {result && (
        <div className={`send-result-toast ${result.success ? 'send-result-toast-success' : 'send-result-toast-error'}`}>
          <span className="send-result-toast-icon">
            {result.success ? '✔️' : '❌'}
          </span>
          {result.success ? 'Evaluación configurada y emails enviados correctamente' : 'Error al configurar evaluación'}
        </div>
      )}

      <div style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 2px 12px rgba(25,179,148,0.08)',
        padding: '32px 0',
        width: '98%',
        maxWidth: 'none',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {step === 'evaluators' ? (
          <PersonCompaniesTable
            selectedRows={selectedEvaluators}
            setSelectedRows={setSelectedEvaluators}
            handleCheckboxChange={handleEvaluatorSelection}
            enviadosEmail={new Set()}
            setShared={() => { }}
            title="Selecciona los evaluadores"
            enablePerformance={enablePerformance}
          />
        ) : (
          <>
            <div style={{ width: '98%', marginBottom: '20px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <h3 style={{ color: '#19B394', marginBottom: '8px' }}>Evaluadores seleccionados</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedEvaluators.map((evaluator, idx) => {
                    const dni = evaluator.dni || String(evaluator.id || '');
                    const active = String(currentEvaluatorDni) === String(dni);
                    return (
                      <button key={idx} onClick={() => handleChangeCurrentEvaluator(dni)} style={{
                        background: active ? '#19B394' : '#e6fffa',
                        color: active ? 'white' : '#0ea5a5',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        border: '1px solid #19B394',
                        cursor: 'pointer'
                      }}>
                        {evaluator.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ flex: 2, minWidth: 260 }}>
                <h3 style={{ color: '#19B394', marginBottom: '8px' }}>Evaluados asignados al evaluador actual</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedEvaluatees.map((evaluatee, idx) => (
                    <span key={idx} style={{
                      background: '#0ea5a5',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      {evaluatee.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <PersonCompaniesTable
              selectedRows={selectedEvaluatees}
              setSelectedRows={setSelectedEvaluatees}
              handleCheckboxChange={handleEvaluateeSelection}
              disabledDniSet={currentEvaluatorDni && alreadyAssignedMap[String(currentEvaluatorDni)] ? alreadyAssignedMap[String(currentEvaluatorDni)] : new Set()}
              enviadosEmail={new Set()}
              setShared={() => { }}
              title={selectedEvaluators.length > 0 ? `Selecciona los evaluados para: ${(
                selectedEvaluators.find(e => String(e.dni || e.id) === String(currentEvaluatorDni))?.name || ''
              )}` : 'Selecciona los evaluados'}
              enablePerformance={enablePerformance}
            />
            {/* Resumen eliminado según solicitud */}
          </>
        )}
      </div>
    </>
  );
};

// Componente para mostrar evaluaciones de desempeño
const PerformanceEvaluationsTable = ({ surveyId, enablePerformance, refreshTrigger, surveyTitle }) => {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  // filas planas para la tabla consolidada
  const [flatRows, setFlatRows] = useState([]);

  const loadEvaluations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/performance-evaluation/${surveyId}`);
      const data = response.data || {};
      if (data.evaluators && data.evaluatees) {
        const evaluateeByDni = new Map((data.evaluatees || []).map(e => [String(e.dni), e]));
        const evaluatorByDni = new Map((data.evaluators || []).map(e => [String(e.dni), e]));
        const statusToLabel = (s) => s === 'completed' ? 'Completado' : (s === 'in_progress' ? 'En Curso' : 'Pendiente');

        // Mantener agrupado por evaluador (para algunas acciones), pero también construir filas planas
        const evaluatorsWithEvaluatees = (data.evaluators || []).map(evaluator => {
          const pairs = (data.evaluations || []).filter(p => String(p.evaluatorId) === String(evaluator.dni));
          const assignedEvaluatees = pairs.map(p => {
            const base = evaluateeByDni.get(String(p.evaluateeId));
            const fallback = { dni: String(p.evaluateeId), name: String(p.evaluateeId) };
            return { ...(base || fallback), estado: statusToLabel(p.status), token: p.token };
          });
          return { evaluator, evaluatees: assignedEvaluatees, evaluations: pairs };
        });
        setEvaluations(evaluatorsWithEvaluatees);

        const rows = (data.evaluations || []).map(p => {
          const evaluator = evaluatorByDni.get(String(p.evaluatorId)) || { dni: String(p.evaluatorId), name: String(p.evaluatorId) };
          const evaluatee = evaluateeByDni.get(String(p.evaluateeId)) || { dni: String(p.evaluateeId), name: String(p.evaluateeId) };
          return {
            pair: p,
            evaluator,
            evaluatee,
            estado: statusToLabel(p.status),
            fecha: p.submittedAt ? new Date(p.submittedAt) : null
          };
        });
        setFlatRows(rows);
      } else {
        setEvaluations([]);
        setFlatRows([]);
      }
    } catch (error) {
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  }, [surveyId]);

  useEffect(() => {
    if (surveyId) {
      loadEvaluations();
    }
  }, [surveyId, refreshTrigger, loadEvaluations]);

  // Exportar consolidado desde backend (Excel con columnas por ítems)
  const exportAllToExcel = useCallback(async () => {
    try {
      const response = await api.get(`/api/performance-evaluation/export-all/${surveyId}`, { responseType: 'blob' });
      const contentType = response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeTitle = (String(surveyTitle || '').trim() || 'evaluacion').replace(/[\\/:*?"<>|]/g, '-');
      link.href = url;
      link.setAttribute('download', `${safeTitle}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('No se pudo exportar a Excel');
    }
  }, [surveyId, surveyTitle]);

  // Listener para botón Excel del título superior (usa la versión memorizada)
  useEffect(() => {
    const handler = () => exportAllToExcel();
    window.addEventListener('pe-export-excel', handler);
    return () => window.removeEventListener('pe-export-excel', handler);
  }, [exportAllToExcel]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px', color: '#19B394' }}>Cargando evaluaciones...</div>;
  }

  if ((!evaluations || evaluations.length === 0) || (flatRows.length === 0)) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        color: '#666',
        background: '#f8f9fa',
        borderRadius: '8px',
        margin: '20px 0'
      }}>
        <h3 style={{ color: '#19B394', marginBottom: '10px' }}>No hay evaluaciones configuradas</h3>
        <p>Configure evaluadores y evaluados en la pestaña "Compartir" para comenzar las evaluaciones de desempeño.</p>
      </div>
    );
  }

  // Helper para descargar Excel por par usando el token directamente
  const exportEvaluateeByToken = async (pair, evaluatee) => {
    try {
      if (!pair?.token) {
        toast.error('No se encontró el token de evaluación');
        return;
      }
      const response = await api.get(`/api/performance-evaluation/download-excel/${surveyId}/${pair.token}`, { responseType: 'blob' });
      const contentType = (response.headers && response.headers['content-type']) || '';
      const blob = new Blob([response.data], { type: contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evaluacion_${evaluatee?.name || 'resultado'}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo descargar el Excel');
    }
  };

  // Helper para descargar PDF por par usando el token directamente
  const exportEvaluateePDFByToken = async (pair, evaluatee) => {
    try {
      if (!pair?.token) {
        toast.error('No se encontró el token de evaluación');
        return;
      }
      const response = await api.get(`/api/performance-evaluation/download-pdf/${surveyId}/${pair.token}`, { responseType: 'blob' });
      const contentType = (response.headers && response.headers['content-type']) || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evaluacion_${evaluatee?.name || 'resultado'}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo descargar el PDF');
    }
  };

  const removeEvaluator = async (dni, force = false) => {
    try {
      const url = `/api/performance-evaluation/${surveyId}/evaluator/${dni}${force ? '?force=true' : ''}`;
      await api.delete(url);
      await loadEvaluations();
      toast.success(force ? 'Evaluador eliminado (incluyendo evaluaciones activas)' : 'Evaluador eliminado');
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.requiresConfirmation) {
        // Show confirmation dialog
        const confirmed = window.confirm(
          `⚠️ ADVERTENCIA\n\n${e.response.data.details}\n\nEsta acción eliminará permanentemente:\n- El evaluador\n- Todas sus evaluaciones (${e.response.data.nonPendingCount} en curso/completadas)\n- Los datos no se podrán recuperar\n\n¿Está seguro de continuar?`
        );
        if (confirmed) {
          await removeEvaluator(dni, true); // Retry with force=true
        }
      } else {
        toast.error(e.response?.data?.message || 'No se pudo eliminar el evaluador');
      }
    }
  };

  const removeEvaluatee = async (evaluatorDni, evaluateeDni, force = false) => {
    try {
      const url = `/api/performance-evaluation/${surveyId}/evaluator/${evaluatorDni}/evaluatee/${evaluateeDni}${force ? '?force=true' : ''}`;
      await api.delete(url);
      await loadEvaluations();
      toast.success(force ? 'Evaluado eliminado (incluyendo evaluación activa)' : 'Evaluado eliminado');
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.requiresConfirmation) {
        // Show confirmation dialog
        const confirmed = window.confirm(
          `⚠️ ADVERTENCIA\n\n${e.response.data.details}\n\nEsta acción eliminará permanentemente:\n- La relación evaluador-evaluado\n- La evaluación en estado "${e.response.data.status}"\n- Los datos no se podrán recuperar\n\n¿Está seguro de continuar?`
        );
        if (confirmed) {
          await removeEvaluatee(evaluatorDni, evaluateeDni, true); // Retry with force=true
        }
      } else {
        toast.error(e.response?.data?.message || 'No se pudo quitar el evaluado');
      }
    }
  };

  const exportEvaluatorToExcel = (evaluation) => {
    try {
      const headers = ['Evaluado', 'DNI', 'Correo', 'Estado'];
      const rows = evaluation.evaluatees.map(e => [e.name, e.dni, e.email, e.estado || 'Pendiente']);
      const csv = [headers, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evaluador_${evaluation.evaluator?.dni}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('No se pudo exportar a Excel');
    }
  };

  const exportEvaluatorToPDF = (evaluation) => {
    try {
      const printContent = document.createElement('div');
      printContent.innerHTML = `
        <div style="font-family: Arial, sans-serif; padding: 16px;">
          <div style="display:flex; align-items:center; gap:12px; margin-bottom:12px;">
            <img src="/logo.png" alt="logo" style="height:28px;" />
            <h2 style="margin:0; color:#19B394;">Evaluador: ${evaluation.evaluator?.name || ''} (${evaluation.evaluator?.dni || ''})</h2>
          </div>
          <table style="width:100%; border-collapse:collapse; font-size:12px;">
            <thead>
              <tr>
                <th style="text-align:left; border-bottom:1px solid #ccc; padding:6px;">Evaluado</th>
                <th style="text-align:center; border-bottom:1px solid #ccc; padding:6px;">DNI</th>
                <th style="text-align:center; border-bottom:1px solid #ccc; padding:6px;">Puesto</th>
                <th style="text-align:center; border-bottom:1px solid #ccc; padding:6px;">Correo</th>
                <th style="text-align:center; border-bottom:1px solid #ccc; padding:6px;">Estado</th>
              </tr>
            </thead>
            <tbody>
              ${evaluation.evaluatees.map(e => `
                <tr>
                  <td style="padding:6px;">${e.name}</td>
                  <td style="padding:6px; text-align:center;">${e.dni}</td>
                  <td style="padding:6px; text-align:center;">${e.jobPosition || ''}</td>
                  <td style="padding:6px; text-align:center;">${e.email}</td>
                  <td style="padding:6px; text-align:center;">${e.estado || 'Pendiente'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>`;
      const win = window.open('', '_blank');
      win.document.write(`<!doctype html><html><head><title>Evaluador ${evaluation.evaluator?.dni}</title></head><body></body></html>`);
      win.document.body.appendChild(printContent);
      setTimeout(() => {
        win.print();
        win.close();
      }, 300);
    } catch (err) {
      toast.error('No se pudo exportar a PDF');
    }
  };

  const exportEvaluateeToExcel = async (evaluation, evaluatee) => {
    try {
      // Buscar el token de la evaluación para este evaluado específico
      const pair = evaluation.evaluations?.find(p => String(p.evaluateeId) === String(evaluatee.dni));
      if (!pair || !pair.token) {
        toast.error('No se encontró la evaluación para este evaluado');
        return;
      }

      // Descargar Excel usando el endpoint del backend
      const response = await api.get(`/api/performance-evaluation/download-excel/${surveyId}/${pair.token}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evaluacion_${evaluatee.name}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Excel descargado correctamente');
    } catch (error) {
      console.error('Error descargando Excel:', error);
      if (error.response?.status === 404) {
        toast.error('La evaluación no está completada aún');
      } else {
        toast.error('Error al descargar Excel');
      }
    }
  };

  const exportEvaluateeToPDF = async (evaluation, evaluatee) => {
    try {
      // Buscar el token de la evaluación para este evaluado específico
      const pair = evaluation.evaluations?.find(p => String(p.evaluateeId) === String(evaluatee.dni));
      if (!pair || !pair.token) {
        toast.error('No se encontró la evaluación para este evaluado');
        return;
      }

      // Descargar PDF usando el endpoint del backend
      const response = await api.get(`/api/performance-evaluation/download-pdf/${surveyId}/${pair.token}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evaluacion_${evaluatee.name}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF descargado correctamente');
    } catch (error) {
      console.error('Error descargando PDF:', error);
      if (error.response?.status === 404) {
        toast.error('La evaluación no está completada aún');
      } else {
        toast.error('Error al descargar PDF');
      }
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Encabezado pequeño eliminado para evitar duplicado del título */}
      <style>{`
        .pe-table th { font-size: 13px; }
        .pe-table td { font-size: 13px; }
        .danger-btn { 
          background: #fee2e2; 
          border: 1px solid #fca5a5; 
          color: #991b1b; 
          padding: 6px 10px; 
          border-radius: 6px; 
          cursor: pointer; 
          transition: all .15s ease-in-out;
        }
        .danger-btn:hover { 
          background: #fecaca; 
          border-color: #f87171; 
          color: #7f1d1d;
        }
        .excel-btn {
          background: transparent;
          border: none;
          padding: 4px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: transform .12s ease-in-out;
        }
        .excel-btn:hover { transform: scale(1.06); }
      `}</style>
      <div className="pe-table" style={{ overflowX: 'auto', borderRadius: 12, background: '#fff', boxShadow: '0 2px 8px rgba(25,179,148,0.08)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
          <thead>
            <tr style={{ background: '#EDF9F7', borderBottom: '2px solid #19B394', color: '#19B394' }}>
              <th style={{ padding: 10, textAlign: 'center' }}>Área</th>
              <th style={{ padding: 10, textAlign: 'center' }}>DNI Evaluado</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Apellido y Nombre Evaluado</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Puesto Trabajo Evaluado</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Fecha de Evaluación</th>
              <th style={{ padding: 10, textAlign: 'center' }}>DNI Evaluador</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Apellido y Nombre Evaluador</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Puesto Trabajo Evaluador</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Estado</th>
              <th style={{ padding: 10, textAlign: 'center' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {flatRows.map((row, idx) => {
              const isCompleted = (row.estado || '').toLowerCase() === 'completado';
              return (
                <tr key={row.pair?.token || `${row.evaluator?.dni}_${row.evaluatee?.dni}_${idx}`} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 10, textAlign: 'center' }}>{row.evaluatee?.area || ''}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>{row.evaluatee?.dni || ''}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>{row.evaluatee?.name || ''}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>{row.evaluatee?.jobPosition || ''}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>{row.fecha ? row.fecha.toLocaleDateString() : ''}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>{row.evaluator?.dni || ''}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>{row.evaluator?.name || ''}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>{row.evaluator?.jobPosition || ''}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>
                    {(() => {
                      const status = (row.estado || '').toLowerCase();
                      const config = status === 'completado'
                        ? { bg: '#d1fae5', color: '#16a34a', text: 'Completado' }
                        : status === 'en curso' || status === 'in_progress'
                          ? { bg: '#dbeafe', color: '#1d4ed8', text: 'En curso' }
                          : { bg: '#e5e7eb', color: '#374151', text: 'Pendiente' };
                      return (
                        <span style={{
                          background: config.bg,
                          color: config.color,
                          padding: '4px 10px',
                          borderRadius: 999,
                          fontWeight: 600,
                          fontSize: 12
                        }}>{config.text}</span>
                      );
                    })()}
                  </td>
                  <td style={{ padding: 10, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
                      <button
                        onClick={() => {
                          if (!isCompleted) { toast.error('La evaluación no está completada aún'); return; }
                          exportEvaluateePDFByToken(row.pair, row.evaluatee);
                        }}
                        title={!isCompleted ? 'La evaluación no está completada aún' : 'Descargar PDF'}
                        className="download-btn"
                        aria-label="Descargar PDF"
                        style={{ opacity: isCompleted ? 1 : 0.5, cursor: isCompleted ? 'pointer' : 'not-allowed' }}
                      >
                        <img src={process.env.PUBLIC_URL + '/icons/pdf.png'} alt="PDF" className="download-icon" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.src = process.env.PUBLIC_URL + '/icons/pdf.svg'; }} />
                      </button>
                      <button
                        onClick={() => removeEvaluatee(row.evaluator?.dni, row.evaluatee?.dni)}
                        title="Eliminar"
                        aria-label="Eliminar"
                        className="danger-btn"
                      >
                        ✖
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SurveyBuilder;
